/**
 * WORLD — toàn bộ mô phỏng trận đấu. LOGIC THUẦN:
 *   - không import Phaser
 *   - tất định (RNG có seed) ⇒ chạy 10.000 trận headless được
 *   - tick cố định 30Hz
 */
import { Rng } from './rng';
import {
  computeDamage,
  DT,
  effectiveStat,
  effectiveStats,
  addBuff,
  isInvulnerable,
  purgeExpiredBuffs,
  enemyGold,
  enrageMultiplier,
  ENRAGE_AFTER_SEC,
  DEF_WEIGHT,
} from './formulas';
import type {
  Buff,
  ClassId,
  Core,
  EffectBurst,
  FloatText,
  Projectile,
  Stats,
  Unit,
} from './types';
import { CLASSES, CLASS_ORDER, type SkillDef } from '../data/classes';
import { MAX_EQUIPPED_SKILLS, skillById } from '../data/skills-extra';
import { ENEMIES, BOSS_AFFIXES, STAGE_BOSS, type EnemyDef } from '../data/enemies';
import { buildWaves, type WaveDef } from '../data/waves';
import { POTIONS, MAX_STAMINA_POTIONS_PER_STAGE, AMMO_PACK_PRICE, AMMO_PACK_SIZE, type PotionKind } from '../data/items';
import { animOf } from '../data/skill-anims';

export const ARENA = { w: 1000, h: 600, coreX: 500, coreY: 300 };

/**
 * VÙNG CHÁY còn hiệu lực trên sân (ultimate Ném Bom Xăng của Dân chợ búa).
 * `until` là mốc giây sim hết cháy; lớp hiệu ứng đọc `fireZonesActive` để vẽ vòng lửa.
 */
export interface FireZone {
  x: number;
  y: number;
  radius: number;
  until: number;
  ownerId: number;
}
export const CORE_MAX_HP = 4200;
export const MAX_HEROES_ON_FIELD = 5;
/**
 * TRẦN SỐ ĐỆ TỬ của "Đẻ mướn" — NGƯỜI DÙNG CHỐT: tối đa 15 con.
 *
 * Vì sao đổi từ 20/1 tướng sang 15 TỔNG: trước đây trần tính theo số tướng nên 2–5 Mẹ
 * Thiên Hạ là 40–100 con ⇒ sân đầy, va chạm đẩy nhau liên tục (kẹt cứng) và FPS sụp.
 * 15 là con số người dùng yêu cầu, và cũng đủ để "lấy lượng bù chất".
 */
export const MAX_MINIONS_TOTAL = 15;

/**
 * VÀNG BẮT ĐẦU MỖI TRẬN.
 *
 * Người dùng: "khi bắt đầu trận, ví trong game start 300, số character bằng 0, phải mua ít nhất
 * 1 character mới bắt đầu màn chơi được. Nếu không đủ tiền mua tướng mà chưa qua màn thì thua luôn".
 *
 * Vì sao 300: tướng đầu tiên của một class giá **200 vàng** (`heroPrice` với `owned = 0`), nên
 * 300 đủ mua đúng 1 tướng và còn 100 để bắt đầu. Đây là con số người chơi chốt, không phải suy diễn.
 */
export const START_RUN_GOLD = 300;

/**
 * NHỊP GIỮA 2 LẦN AUTO NIỆM CHIÊU (giây).
 * Bot chọn chiêu tốt nhất rồi CHỜ, thay vì xả cả 7 chiêu trong vài tick.
 * ⚠️ ĐO ĐƯỢC: để 0,45s thì DPS của tướng tụt mạnh (tỉ lệ qua màn 95% → 12,5% khi đo bằng
 * `npm run balance`) — "thông minh" không có nghĩa là niệm ÍT. 0,2s vẫn đủ để không xả
 * hết 7 chiêu trong 1 tick (30Hz ⇒ 1 chiêu/6 tick) mà không mất sát thương.
 */
export const AUTO_CAST_GAP = 0.15;

export type WorldStatus = 'prep' | 'fighting' | 'waveClear' | 'won' | 'lost';

export interface LineupEntry {
  heroId: string;
  classId: ClassId;
  name: string;
  stats: Stats;
  /** bộ phụ kiện đang mặc — chỉ dùng cho hiển thị, không ảnh hưởng mô phỏng */
  loadout?: Record<string, string>;
}

export interface SimEvent {
  type: 'hit' | 'cast' | 'die' | 'potion' | 'waveStart' | 'waveClear' | 'coreHit' | 'buy' | 'error';
  x?: number;
  y?: number;
  text?: string;
  power?: number;
}

export interface WorldOptions {
  seed?: number;
  stageIndex?: number;
  lineup: LineupEntry[];
  autoCast?: boolean;
  /**
   * SKILL ĐANG TRANG BỊ theo class (id, tối đa 7 ô) — màn nào cũng dùng khi tạo World.
   * Thiếu/ rỗng thì rơi về 7 chiêu gốc trong `CLASSES[classId].skills`.
   */
  skillLoadout?: Partial<Record<string, string[]>>;
  /**
   * HỆ SỐ ĐỘ KHÓ của màn (easy/ normal/ elite) — nhân vào máu/ sát thương/ vàng của quái.
   * Nhân ở đây (sau `buildWaves`) để `buildWaves(stageIndex)` giữ nguyên chữ ký cho
   * `tools/balance.ts` và test cũ.
   */
  diffMult?: { hp: number; atk: number; gold: number };
}

let nextId = 1;

/** Đơn vị này có phải TƯỚNG theo class (không phải đệ tử triệu hồi)? */
export function isClassHero(u: Unit): boolean {
  return u.team === 'hero' && (CLASSES as Record<string, unknown>)[u.kind] !== undefined;
}

/**
 * GHÉP BỘ SKILL ĐANG TRANG BỊ thành danh sách có thứ tự (nguồn sự thật của 7 ô).
 * Id lạ/ trùng bị bỏ; rỗng thì rơi về bộ mặc định của class. Tối đa `MAX_EQUIPPED_SKILLS`.
 */
function resolveSkillLoadout(classId: string, ids?: string[]): SkillDef[] {
  const base = CLASSES[classId as ClassId]?.skills ?? [];
  if (!ids || ids.length === 0) return base;
  const out: SkillDef[] = [];
  for (const id of ids) {
    const sk = skillById(id) ?? base.find((b) => b.id === id);
    if (!sk || out.some((o) => o.id === sk.id)) continue;
    out.push(sk);
    /*
     * ⚠️⚠️ PHẢI dùng `MAX_EQUIPPED_SKILLS`, KHÔNG hard-code 7.
     *
     * LỖI THẬT ĐÃ ĐO ĐƯỢC (teammate `skill-cap-test` phát hiện): chỗ này cứng `7` nên khi trần đổi
     * thành 3, **SÂN ĐẤU vẫn niệm 7 chiêu trong khi HUD chỉ cho 3 ô** — lọt qua save cũ chưa migrate
     * hoặc ai gọi `new World(...)` trực tiếp. Đường đi chuẩn (`game.ts → equippedSkills()`) đã đúng
     * nên lỗi này ẩn rất kỹ.
     */
    if (out.length >= MAX_EQUIPPED_SKILLS) break;
  }
  return out.length ? out : base;
}

export class World {
  /** Nhịp riêng của ultimate theo unit (nuốt/ ném bom/ máy bay) — tách khỏi state chung. */
  private ultState = new Map<
    number,
    { devourAt: number; bombAt: number; airAt: number; planeX: number; planeDir: 1 | -1; bombZone: number }
  >();
  /** unit bị thu phục → id chủ nhân (ultimate Thu Phục) */
  private convertedOwners = new Map<number, number>();
  /** vùng đã ném bom xăng gần nhất (tránh ném 2 lần vào cùng chỗ) */
  private lastBombZone = -1;
  /**
   * VÙNG CHÁY do chai rượu để lại (ultimate Street).
   * Người dùng: *"có hiệu ứng cháy phạm vi ảnh hưởng"* ⇒ vùng này vừa gây DOT cho quái đi vào,
   * vừa là dữ liệu để lớp hiệu ứng vẽ vòng lửa (không vẽ rời ở client).
   */
  private fireZones: FireZone[] = [];
  /** gốc quay vòng tuần tra của Street khi ultimate đang chạy (mỗi tướng một pha) */
  private ultPatrol = new Map<number, number>();

  time = 0;
  wave = 0;
  stageIndex: number;
  units: Unit[] = [];
  projectiles: Projectile[] = [];
  floats: FloatText[] = [];
  bursts: EffectBurst[] = [];
  /**
   * "ĐỨNG HÌNH VA CHẠM" (hit-stop) — giây mà lớp hiển thị NÊN khựng lại.
   * Mô phỏng chỉ ĐẶT cờ, không tự đọc ⇒ vẫn tất định và chạy headless bình thường.
   */
  hitStop = 0;
  /** sức mạnh vụ nổ gần nhất (0..1) — render dùng để rung màn hình */
  shockPower = 0;
  private burstId = 1;
  /**
   * RNG RIÊNG cho hiệu ứng.
   *
   * ⚠️ BẪY ĐÃ SẬP: nếu hiệu ứng dùng chung `this.rng` thì mỗi vụ nổ lại "ăn" một số
   * ngẫu nhiên ⇒ lệch toàn bộ chuỗi RNG phía sau ⇒ kết quả trận đấu đổi khác
   * (test "wave 1 Nhà nguyên vẹn" đổ ngay). Hiệu ứng phải có nguồn ngẫu nhiên riêng.
   */
  private fxRng = new Rng(0x5eed1);
  core: Core = { x: ARENA.coreX, y: ARENA.coreY, hp: CORE_MAX_HP, maxHp: CORE_MAX_HP, radius: 46, shield: 0 };
  /**
   * VÍ TRONG TRẬN. Khởi tạo bằng `START_RUN_GOLD` (xem chú thích hằng số đó).
   */
  gold = START_RUN_GOLD;
  /**
   * KIM CƯƠNG KIẾM TRONG TRẬN — ví RIÊNG, khởi tạo 0 mỗi lần vào trận.
   *
   * Người dùng: "tiền và kim cương reset về 0 và sau khi hoàn thành màn mới cộng vào số tổng
   * đang có (không hoàn thành ải không được thưởng)". Kim cương chỉ được cộng vào ví tổng khi
   * QUA MÀN (xem `Game.onStatusChange`), thua thì mất.
   */
  diamonds = 0;
  status: WorldStatus = 'prep';
  potions: Record<PotionKind, number>;
  staminaPotionsUsed = 0;
  events: SimEvent[] = [];
  waves: WaveDef[];
  rng: Rng;
  autoCast = true;
  /** skill đang trang bị theo class — nguồn sự thật cho castSkill/ tryAutoCast/ HUD */
  private skillsByClass = new Map<string, SkillDef[]>();
  /** thống kê để hiển thị + cân bằng */
  stats = { kills: 0, damageDealt: 0, damageTaken: 0, damageFloored: 0, blocked: 0, shots: 0, coreDamage: 0 };

  private spawnQueue: Array<{ at: number; type: string; x: number; y: number; scale: number; atkScale: number; boss?: { name: string; mult: number; special: string; defMult: number } }> = [];
  private waveStartedAt = 0;
  private waveDef: WaveDef | null = null;
  private heroHome: Map<number, { x: number; y: number }> = new Map();
  private lineup: LineupEntry[];

  /**
   * ĐỘI HÌNH ĐANG THỰC SỰ CÓ TRÊN SÂN (không phải danh sách tướng đã mua).
   *
   * Người dùng: mỗi trận bắt đầu sân TRỐNG, tướng được mua dần trong trận. Vì vậy tầng hiển thị
   * phải dựng rig theo ĐÚNG danh sách này — nếu dùng `meta.heroes` (danh sách vĩnh viễn) thì
   * những tướng CHƯA mua trong trận sẽ bị kéo ra sân.
   */
  get fieldedLineup(): readonly LineupEntry[] {
    return this.lineup;
  }
  private bossSpawned = false;

  constructor(opts: WorldOptions) {
    this.rng = new Rng(opts.seed ?? 1234);
    this.stageIndex = opts.stageIndex ?? 0;
    this.waves = buildWaves(this.stageIndex);
    // độ khó của màn: nhân vào chỉ số quái (xem `diffMult` trong WorldOptions)
    if (opts.diffMult) {
      const d = opts.diffMult;
      for (const w of this.waves) {
        w.hpScale *= d.hp;
        w.atkScale *= d.atk;
        w.rewardGold = Math.round(w.rewardGold * d.gold);
      }
    }
    this.lineup = opts.lineup.slice(0, MAX_HEROES_ON_FIELD);
    this.autoCast = opts.autoCast ?? true;
    for (const entry of this.lineup) {
      this.skillsByClass.set(entry.classId, resolveSkillLoadout(entry.classId, opts.skillLoadout?.[entry.classId]));
    }
    this.potions = { hp: 0, mana: 0, stamina: 0 };
    this.spawnHeroes();
  }

  // ------------------------------------------------------------ khởi tạo

  private spawnHeroes(): void {
    const n = this.lineup.length;
    this.lineup.forEach((entry, i) => {
      // xếp thành VÒNG CUNG quanh Nhà, đủ xa để không đứng trên mái nhà
      const angle = -Math.PI / 2 + (i - (n - 1) / 2) * 0.62;
      const dist = 152 + (i % 2) * 26;
      const x = this.core.x + Math.cos(angle) * dist * 1.15;
      const y = this.core.y + Math.sin(angle) * dist + 26;
      const unit = this.makeUnit(entry.classId, 'hero', x, y, entry.stats, 20);
      unit.homeX = unit.x;
      unit.homeY = unit.y;
      unit.ammo = CLASSES[entry.classId].usesAmmo ? 60 : 0;
      this.heroHome.set(unit.id, { x: unit.x, y: unit.y });
      this.units.push(unit);
    });
  }

  private makeUnit(kind: string, team: 'hero' | 'enemy', x: number, y: number, stats: Stats, radius: number): Unit {
    return {
      id: nextId++,
      team,
      kind,
      isBoss: false,
      isElite: false,
      tier: 0,
      x,
      y,
      homeX: x,
      homeY: y,
      radius,
      base: { ...stats },
      hp: stats.hp,
      mana: stats.mana,
      stamina: stats.stamina,
      attackCd: 0,
      targetId: null,
      state: 'idle',
      facing: 1,
      swing: 0,
      attackMove: 0,
      castAnim: null,
      castT0: 0,
      castDur: 0,
      lastComboRep: -1,
      orderX: null,
      orderY: null,
      hitFlash: 0,
      invulnUntil: 0,
      // ULTIMATE: chưa dùng (xem `castUltimate`) — hồi xong ngay từ đầu trận
      ultUntil: 0,
      ultReadyAt: 0,
      stunUntil: 0,
      isClone: false,
      isConverted: false,
      buffs: [],
      shield: 0,
      shieldUntil: 0,
      alive: true,
      ammo: 0,
      summonCd: CLASSES[kind as ClassId]?.summonEvery ?? 0,
      minions: 0,
      skillCds: [],
      scale: 1,
      deadAt: 0,
    };
  }

  get heroes(): Unit[] {
    return this.units.filter((u) => u.team === 'hero' && u.alive);
  }

  /** Chỉ TƯỚNG theo class (không tính đệ tử triệu hồi). */
  /** Số giây đã trôi qua trong wave hiện tại. */
  get secondsInWave(): number {
    return this.status === 'fighting' ? this.time - this.waveStartedAt : 0;
  }

  /** Wave này đã bị cuồng nộ chưa (quái mạnh dần để chống treo màn). */
  get isEnraged(): boolean {
    return this.secondsInWave > ENRAGE_AFTER_SEC;
  }

  get classHeroes(): Unit[] {
    /*
     * ⚠️ LOẠI BẢN SAO (Phân Thân) — người dùng chốt **(B)**: bản sao tự đánh, chỉ con gốc được
     * điều khiển. Nếu tính bản sao vào đây thì bật ultimate là sân "đầy 5" và người chơi KHÔNG MUA
     * ĐƯỢC TƯỚNG NÀO NỮA trong suốt 20s — hỏng luật "mỗi màn 5 tướng".
     * Quân THU PHỤC cũng bị loại: nó là quái cũ đổi phe, không phải tướng người chơi mua.
     */
    return this.units.filter((u) => u.team === 'hero' && u.alive && isClassHero(u) && !u.isClone && !u.isConverted);
  }

  /** Thêm tướng vào giữa trận (khi người chơi vừa mua). */
  addHeroToBattle(entry: LineupEntry): Unit {
    const n = this.lineup.length;
    const angle = -Math.PI / 2 + (n - 1) * 0.62;
    const dist = 152 + (n % 2) * 26;
    const x = this.core.x + Math.cos(angle) * dist * 1.15;
    const y = this.core.y + Math.sin(angle) * dist + 26;
    const unit = this.makeUnit(entry.classId, 'hero', x, y, entry.stats, 20);
    unit.homeX = unit.x;
    unit.homeY = unit.y;
    unit.ammo = CLASSES[entry.classId].usesAmmo ? 60 : 0;
    this.heroHome.set(unit.id, { x: unit.x, y: unit.y });
    this.units.push(unit);
    this.lineup.push(entry);
    this.burst({ x, y, radius: 50, color: 0xfbbf24, ttl: 0.5, maxTtl: 0.5, kind: 'spawn' });
    return unit;
  }

  /**
   * NGƯỜI CHƠI RA LỆNH DI CHUYỂN cho một tướng (click/ chạm vào sân).
   * Tướng sẽ đi tới đó, rồi lấy đó làm vị trí neo mới để tiếp tục phòng thủ.
   */
  commandMove(unitId: number, x: number, y: number): boolean {
    const u = this.units.find((v) => v.id === unitId && v.team === 'hero' && v.alive);
    if (!u) return false;
    u.orderX = Math.max(24, Math.min(ARENA.w - 24, x));
    u.orderY = Math.max(24, Math.min(ARENA.h - 24, y));
    return true;
  }

  /** Tra đơn vị kể cả đã chết (dùng cho UI). */
  unitByIdPublic(id: number): Unit | undefined {
    return this.units.find((u) => u.id === id);
  }

  /**
   * Áp chỉ số mới cho mọi tướng thuộc 1 class (khi vừa tập luyện / mặc phụ kiện).
   * Máu hiện tại được giữ theo TỈ LỆ để không bị "hồi đầy" miễn phí.
   */
  refreshHeroStats(classId: ClassId, stats: Stats): void {
    for (const u of this.units) {
      if (u.team !== 'hero' || u.kind !== classId) continue;
      const ratio = u.base.hp > 0 ? u.hp / u.base.hp : 1;
      u.base = { ...stats };
      u.hp = Math.max(1, Math.min(stats.hp, stats.hp * ratio));
      u.mana = Math.min(stats.mana, u.mana);
    }
  }

  get enemies(): Unit[] {
    return this.units.filter((u) => u.team === 'enemy' && u.alive);
  }

  private unitById(id: number | null): Unit | undefined {
    if (id === null) return undefined;
    const u = this.units.find((x) => x.id === id);
    return u && u.alive ? u : undefined;
  }

  // ------------------------------------------------------------ điều khiển wave

  /**
   * BỘ SKILL của 1 class trong trận này (7 ô đang trang bị).
   * HUD đọc đúng hàm này ⇒ nút chiêu và ô hồi chiêu luôn khớp thứ tự với sim.
   */
  skillsFor(kind: string): SkillDef[] {
    return this.skillsByClass.get(kind) ?? CLASSES[kind as ClassId]?.skills ?? [];
  }

  /**
   * CÓ ĐƯỢC BẮT ĐẦU WAVE KHÔNG.
   *
   * Người dùng: "số character bằng 0, phải mua ít nhất 1 character mới bắt đầu màn chơi được".
   * Vì vậy điều kiện gồm cả: trên sân phải có ÍT NHẤT 1 TƯỚNG (đệ tử của Mẹ KHÔNG tính — chúng
   * là quân triệu hồi, không phải tướng người chơi mua).
   */
  canStartWave(): boolean {
    if (this.status !== 'prep' && this.status !== 'waveClear') return false;
    if (this.wave >= this.waves.length) return false;
    return this.classHeroes.length > 0;
  }

  /**
   * ⚠️ "KHÔNG ĐỦ TIỀN MUA TƯỚNG ⇒ THUA LUÔN".
   *
   * Người dùng: "nếu không đủ tiền mua tướng mà chưa qua màn thì thua luôn", làm rõ thêm:
   * "cứ không đủ tiền mua character là thua luôn".
   *
   * Điều kiện: CHƯA có tướng nào trên sân VÀ ví trong trận không đủ mua tướng rẻ nhất. Lúc đó
   * người chơi không còn cách nào tiến được (không có tướng thì không bắt đầu wave được, mà
   * không đánh thì không kiếm được tiền) ⇒ thua, thay vì để họ mắc kẹt mãi ở màn `prep`.
   */
  checkBankrupt(): boolean {
    if (this.status === 'won' || this.status === 'lost') return false;
    if (this.classHeroes.length > 0) return false;
    if (this.gold >= this.cheapestHeroPrice()) return false;
    this.lose();
    this.events.push({ type: 'error', text: 'Hết tiền mua tướng — thua màn!' });
    return true;
  }

  /**
   * SỐ TƯỚNG CÙNG CLASS ĐÃ MUA **TRONG TRẬN NÀY**.
   *
   * ⚠️ ĐÂY LÀ CƠ SỞ TÍNH GIÁ, KHÔNG PHẢI DANH SÁCH TƯỚNG VĨNH VIỄN Ở TRANG CHỦ.
   *
   * Người dùng: "khi bắt đầu vào trận, chưa có tướng, tất cả các tướng đều giá 200 vì chưa
   * được mua lần nào".
   *
   * Vì mỗi trận bắt đầu với sân TRỐNG, số tướng đang có trên sân CHÍNH LÀ số đã mua trong trận.
   * Nhờ đó: đầu trận mọi class đều 200; và không còn cảnh người chơi đã sở hữu tướng ở nhà nên
   * vào trận thấy giá 430 — trái với điều người dùng nói.
   * (Đếm theo `kind` của đơn vị đang sống trên sân; đệ tử của Mẹ không phải tướng nên không tính.)
   */
  runOwnedHeroCounts(): Partial<Record<ClassId, number>> {
    const counts: Partial<Record<ClassId, number>> = {};
    for (const u of this.units) {
      if (u.team !== 'hero' || !isClassHero(u)) continue;
      const c = u.kind as ClassId;
      counts[c] = (counts[c] ?? 0) + 1;
    }
    return counts;
  }

  /**
   * Giá tướng RẺ NHẤT có thể mua lúc này — TÍNH TỪ SỐ ĐÃ MUA TRONG TRẬN, KHÔNG hằng số.
   *
   * ⚠️ Bản đầu tôi viết `return 200` (hằng số). Sai theo hai hướng: (a) khi đã mua nhiều tướng
   * trong trận thì giá thật cao hơn, và (b) nếu để hằng số thì luật "hết tiền ⇒ thua" tính sai —
   * người chơi có thể mắc kẹt ở màn chuẩn bị vì không mua được ai mà cũng không bị xử thua.
   * Nay tính đúng theo công thức `heroPrice()`: `50·2^owned·4 + owned·30`.
   */
  cheapestHeroPrice(): number {
    return this.heroPricesInRun().min;
  }

  /** Giá tướng theo từng class trong trận này (dùng cho cả luật thua lẫn hiển thị). */
  heroPricesInRun(): { byClass: Partial<Record<ClassId, number>>; min: number } {
    const counts = this.runOwnedHeroCounts();
    const byClass: Partial<Record<ClassId, number>> = {};
    let min = Infinity;
    for (const c of CLASS_ORDER) {
      const owned = counts[c] ?? 0;
      const price = Math.round(50 * Math.pow(2, owned) * 4 + owned * 30);
      byClass[c] = price;
      if (price < min) min = price;
    }
    return { byClass, min: Number.isFinite(min) ? min : 200 };
  }

  startWave(): void {
    if (!this.canStartWave()) return;
    this.wave += 1;
    const def = this.waves[this.wave - 1];
    this.waveDef = def;
    this.status = 'fighting';
    this.waveStartedAt = this.time;
    this.bossSpawned = false;
    this.spawnQueue = [];

    // lối vào: 2 cửa cho màn 1 (trái/phải), quái đi về Nhà
    const gates = [
      { x: -30, y: ARENA.h * 0.5 },
      { x: ARENA.w + 30, y: ARENA.h * 0.5 },
      { x: ARENA.w * 0.5, y: -30 },
      { x: ARENA.w * 0.5, y: ARENA.h + 30 },
    ];
    const gateCount = this.wave >= 7 ? 4 : 2;

    for (const g of def.groups) {
      for (let i = 0; i < g.count; i++) {
        const gate = gates[this.rng.int(0, gateCount - 1)];
        this.spawnQueue.push({
          at: this.time + g.delay + i * g.interval,
          type: g.type,
          x: gate.x + this.rng.range(-24, 24),
          y: gate.y + this.rng.range(-24, 24),
          scale: def.hpScale,
          atkScale: def.atkScale,
        });
      }
    }
    // MỖI WAVE CÓ BOSS
    const boss = def.boss;
    const bossDef: EnemyDef = boss.unique ? STAGE_BOSS : ENEMIES[boss.archetype];
    const bossGate = gates[this.rng.int(0, gateCount - 1)];
    this.spawnQueue.push({
      at: this.time + 2.5,
      type: bossDef.id,
      x: bossGate.x,
      y: bossGate.y,
      scale: boss.unique ? 1 : boss.hpMult * def.hpScale * 0.28,
      atkScale: boss.unique ? 1 : boss.atkMult * def.atkScale * 0.42,
      boss: { name: boss.unique ? boss.name : `${BOSS_AFFIXES.find((a) => a.id === boss.affix)?.name ?? ''} ${bossDef.name}`, mult: boss.hpMult, special: boss.special, defMult: boss.defMult },
    });

    this.events.push({ type: 'waveStart', power: this.wave });
    this.pushFloat(this.core.x, this.core.y - 90, `WAVE ${this.wave}`, 0xfbbf24, 'gold', 1.6);
  }

  // ------------------------------------------------------------ vòng lặp

  update(dt: number): void {
    if (this.status === 'won' || this.status === 'lost') {
      this.updateVisuals(dt);
      return;
    }
    /*
     * KIỂM "HẾT TIỀN MUA TƯỚNG ⇒ THUA" mỗi nhịp khi CHƯA có tướng nào.
     * Đặt ở đây (không phải trong HUD) để luật nằm trong mô phỏng, test được và không phụ thuộc
     * việc người chơi có đang mở giao diện hay không.
     */
    if (this.classHeroes.length === 0 && this.checkBankrupt()) {
      /*
       * `checkBankrupt()` trả `true` khi vừa xử thua. Dùng giá trị trả về thay vì so
       * `this.status === 'lost'`: TypeScript thu hẹp kiểu `status` sau nhánh thoát ở trên nên
       * không thấy khả năng `'lost'` nữa (TS2367) — và cách này cũng rõ nghĩa hơn.
       */
      this.updateVisuals(dt);
      return;
    }
    this.time += dt;
    /*
     * ULTIMATE — chạy TRƯỚC khi tướng hành động để hiệu ứng của khung này có tác dụng ngay:
     * kiểm hết hiệu lực (trả quân thu phục về làm quái), rồi chạy nhịp riêng của từng ultimate.
     */
    this.updateUltimates(dt);
    if (this.status === 'fighting') this.runSpawns();

    for (const u of this.units) {
      if (!u.alive) continue;
      purgeExpiredBuffs(u, this.time);
      this.updateUnit(u, dt);
    }

    this.updateProjectiles(dt);
    this.separateUnits();
    this.cleanupDead();
    this.updateVisuals(dt);

    if (this.status === 'fighting') this.checkWaveEnd();
  }

  private runSpawns(): void {
    if (this.spawnQueue.length === 0) return;
    const due = this.spawnQueue.filter((s) => s.at <= this.time);
    if (due.length === 0) return;
    this.spawnQueue = this.spawnQueue.filter((s) => s.at > this.time);
    for (const s of due) {
      const def = ENEMIES[s.type] ?? STAGE_BOSS;
      const stats: Stats = {
        hp: def.base.hp * s.scale,
        atk: def.base.atk * s.atkScale,
        def: def.base.def * Math.sqrt(s.scale),
        mana: 0,
        stamina: 0,
        atkSpeed: def.base.atkSpeed,
        range: def.base.range,
        moveSpeed: def.base.moveSpeed,
        dodge: def.base.dodge,
        crit: def.base.crit,
        manaRegen: 0,
        staminaRegen: 0,
      };
      const unit = this.makeUnit(def.id, 'enemy', s.x, s.y, stats, def.radius);
      unit.scale = s.scale;
      if (s.boss) {
        unit.isBoss = true;
        unit.isElite = true;
        unit.tier = this.wave;
        unit.base.def *= s.boss.defMult;
        unit.radius = def.radius * 1.15;
        unit.hp = unit.base.hp;
        this.bossSpawned = true;
        this.events.push({ type: 'cast', x: unit.x, y: unit.y, text: s.boss.name, power: 3 });
        this.pushFloat(unit.x, unit.y - 40, s.boss.name, 0xf87171, 'crit', 2.2);
      } else if (this.wave >= 8 && this.rng.chance(0.15)) {
        unit.isElite = true;
        unit.base.hp *= 1.8;
        unit.hp = unit.base.hp;
        unit.base.atk *= 1.3;
        unit.radius *= 1.1;
      }
      unit.facing = unit.x < ARENA.coreX ? 1 : -1;
      this.units.push(unit);
      this.burst({ x: unit.x, y: unit.y, radius: def.radius * 2, color: 0x9333ea, ttl: 0.35, maxTtl: 0.35, kind: 'spawn' });
    }
  }

  private updateUnit(u: Unit, dt: number): void {
    if (u.hitFlash > 0) u.hitFlash = Math.max(0, u.hitFlash - dt * 4);
    if (u.swing > 0) u.swing = Math.max(0, u.swing - dt * 3.2);
    if (u.castAnim && this.time >= u.castT0 + u.castDur) u.castAnim = null;

    /*
     * ĐÒN LIÊN HOÀN: phát một tia ở MỖI CÚ, không chỉ một lần lúc bắt đầu chiêu.
     * Ở cỡ nhân vật trong game (~100px) thì xoay xương thôi KHÔNG đủ để người chơi
     * "thấy" cú đá — cảm giác đòn đến từ hiệu ứng ở đúng chỗ chân/tay vừa đánh tới.
     */
    if (u.castAnim === 'kickCombo' || u.castAnim === 'punchCombo') {
      const reps = u.castAnim === 'kickCombo' ? 4 : 6;
      const prog = (this.time - u.castT0) / Math.max(0.05, u.castDur);
      const rep = Math.floor(prog * reps);
      if (rep !== u.lastComboRep) {
        u.lastComboRep = rep;
        const isKick = u.castAnim === 'kickCombo';
        // chân phải đá ở nhịp chẵn, chân trái ở nhịp lẻ — đổi bên cho khớp dáng
        const side = rep % 2 === 0 ? 1 : -1;
        const hx = u.x + u.facing * (isKick ? 26 : 24);
        const hy = u.y + (isKick ? 4 : -12) + side * (isKick ? 3 : 5);
        // mỗi cú = 1 vệt chém + 1 tia + sốc nhỏ ⇒ liên hoàn "nổ" từng nhịp
        const a = u.facing > 0 ? -0.35 * side : Math.PI + 0.35 * side;
        this.burst({ x: hx, y: hy, radius: isKick ? 30 : 22, color: isKick ? 0x67e8f9 : 0xfbbf24, ttl: 0.24, maxTtl: 0.24, kind: isKick ? 'slash' : 'impact', angle: a + Math.PI * 0.5, len: 40 });
        this.burst({ x: hx, y: hy, radius: isKick ? 20 : 15, color: 0xffffff, ttl: 0.14, maxTtl: 0.14, kind: 'flash', scale: 0.3 });
        this.burst({ x: hx, y: hy, radius: 8, color: isKick ? 0x67e8f9 : 0xfbbf24, ttl: 0.3, maxTtl: 0.3, kind: 'spark', angle: a, len: 34 });
        this.hitStop = Math.max(this.hitStop, 0.02);
      }
    } else if (u.castAnim) {
      u.lastComboRep = -1;
    }
    if (u.attackCd > 0) u.attackCd -= dt;

    const s = effectiveStats(u);

    // hồi mana / stamina
    u.mana = Math.min(s.mana, u.mana + s.manaRegen * dt);
    u.stamina = Math.min(s.stamina, u.stamina + s.staminaRegen * dt);
    if (u.shield > 0 && this.time > u.shieldUntil) u.shield = 0;

    /*
     * CHOÁNG — đơn vị bị choáng KHÔNG hành động (không đi, không đánh, không niệm chiêu).
     * Người dùng: *"gây choáng"*. Boss không bao giờ vào đây vì `applyStun()` từ chối boss.
     * Vẫn hồi mana/ stamina ở trên để khi hết choáng thì đánh tiếp ngay, không bị "đơ" thêm.
     */
    if (!this.isStunned(u)) {
      if (u.team === 'hero') {
        if (isClassHero(u)) this.updateHero(u, s, dt);
        else this.updateAllyMinion(u, s, dt);
      } else this.updateEnemy(u, s, dt);
    }
  }

  /**
   * ĐỆ TỬ của "Mẹ thiên hạ" — melee đơn giản, lao vào quái gần nhất.
   *
   * ⚠️ HAI LỖI NGƯỜI CHƠI BÁO, đã sửa ở đây:
   *  1. "không tấn công thì đi về trung tâm nhưng bị KẸT": bản cũ cho MỌI con đi về
   *     CÙNG MỘT ĐIỂM gần Nhà ⇒ dồn cục, `separateUnits` đẩy qua đẩy lại mà không con nào
   *     nhích được. Nay mỗi con có Ô RIÊNG trên vòng tròn quanh Nhà (`rallySpot`), và có
   *     BẺ LÁI khi phát hiện đứng yên quá 0,7s (đi vòng 0,6s rồi đi tiếp).
   *  2. Đứng chồng lên Nhà: Nhà là vật cản, đệ tử bị đẩy ra mép ⇒ nay tự né trước.
   */
  private updateAllyMinion(u: Unit, s: Stats, dt: number): void {
    // ĐANG BẺ LÁI: đi ngang một nhịp để thoát đám đông/ vật cản
    if ((u.detourUntil ?? 0) > this.time) {
      u.state = 'move';
      const a = u.detourAngle ?? 0;
      this.moveToward(u, u.x + Math.cos(a) * 100, u.y + Math.sin(a) * 100, s.moveSpeed, dt);
      return;
    }

    const target = this.pickNearestEnemy(u, 420);
    if (!target) {
      // KHÔNG CÓ QUÁI: về ĐÚNG Ô TỤ QUÂN của mình quanh Nhà rồi đứng yên
      const spot = this.rallySpot(u);
      const d = this.distXY(u.x, u.y, spot.x, spot.y);
      /*
       * TRỄ (HYSTERESIS) để KHÔNG RUNG: tới trong 18px thì đứng yên, nhưng chỉ đi lại khi
       * đã bị đẩy ra xa hơn 34px. Nếu dùng CÙNG một ngưỡng thì va chạm đẩy ra 19px là con
       * đệ tử lại đi tiếp ⇒ rung liên tục (đo được: trôi 54,9px trong 2 giây).
       */
      const settled = u.state === 'idle';
      if (d > (settled ? 34 : 18)) {
        u.state = 'move';
        this.moveToward(u, spot.x, spot.y, s.moveSpeed, dt);
      } else {
        u.state = 'idle'; // tới nơi ⇒ đứng yên
      }
      this.trackStuck(u, spot.x, spot.y, dt);
      return;
    }
    u.targetId = target.id;
    u.facing = target.x >= u.x ? 1 : -1;
    if (this.dist(u, target) <= s.range + target.radius) {
      u.state = 'attack';
      if (u.attackCd <= 0) {
        u.attackCd = 1 / Math.max(0.15, s.atkSpeed);
        u.swing = 1;
        const ts = effectiveStats(target);
        const res = computeDamage({ atk: s.atk, def: ts.def, targetMaxHp: target.base.hp, isEliteOrBoss: target.isElite || target.isBoss });
        // ⚠️ Đệ tử KHÔNG được đẩy lùi quái — xem `separateUnits` (đệ tử nhường hết va chạm)
        this.applyDamage(target, res.damage, u, false, res.floored);
      }
    } else {
      u.state = 'move';
      this.moveToward(u, target.x, target.y, s.moveSpeed, dt);
      this.trackStuck(u, target.x, target.y, dt);
    }
  }

  /**
   * Phát hiện "đứng yên dù đang đi" ⇒ hẹn bẻ lái.
   * Ngưỡng 0,7s: đủ dài để không bẻ lái vì va chạm nhất thời, đủ ngắn để không thấy kẹt.
   */
  private trackStuck(u: Unit, goalX: number, goalY: number, dt: number): void {
    /*
     * ⚠️ BẪY ĐÃ DÍNH: đo "kẹt" bằng QUÃNG ĐƯỜNG ĐI ĐƯỢC là SAI trong đám đông — 15 con
     * chen nhau thì mỗi con đi rất ít dù vẫn đang tiến về đích ⇒ cờ kẹt bật liên tục,
     * đệ tử cứ 0,6s lại bẻ lái một lần và KHÔNG BAO GIỜ về tới vành tụ quân (đo được:
     * sau 25 giây vẫn còn con ở 199px với `detour=Y` triền miên).
     * Sửa: đo TIẾN ĐỘ TỚI ĐÍCH (khoảng cách còn lại có giảm không) + hồi bẻ lái 2,5s.
     */
    const d = this.distXY(u.x, u.y, goalX, goalY);
    const prev = u.goalDist;
    u.goalDist = d;
    if (prev === undefined || d < prev - 0.5) u.stuckT = 0;
    else u.stuckT = (u.stuckT ?? 0) + dt;
    if ((u.stuckT ?? 0) > 1.2 && (u.detourCdUntil ?? 0) <= this.time) {
      u.stuckT = 0;
      u.detourUntil = this.time + 0.6;
      u.detourCdUntil = this.time + 3; // không bẻ lái liên tục
      /*
       * ⚠️ KHÔNG dùng `this.rng` ở đây. `rng` là chuỗi ngẫu nhiên của LƯỢT CHƠI (vị trí
       * quái, chí mạng…): rút thêm số từ nó làm MỌI kết quả về sau lệch đi — đúng cái bẫy
       * đã dính với hiệu ứng (`World.fxRng`). Góc bẻ lái lấy TẤT ĐỊNH từ ô tụ quân theo
       * góc vàng (2,399 rad ≈ 137,5°) nên các con tản ra đều, và chạy lại ra y hệt.
       */
      u.detourAngle = ((u.minionSlot ?? 0) * 2.399 + this.time * 0.7) % (Math.PI * 2);
    }
  }

  private updateHero(u: Unit, s: Stats, dt: number): void {
    const cls = CLASSES[u.kind as ClassId];

    // auto-cast skill
    if (this.autoCast) this.tryAutoCast(u, s);

    /*
     * "MẸ THIÊN HẠ" ĐẺ QUÂN — 10 giây/ con, CHỈ KHI ĐANG CHIẾN ĐẤU.
     *
     * Người dùng yêu cầu: "khi nâng thời gian mẹ thiên hạ đẻ lên 10s 1 lần, chỉ tính thời gian
     * đẻ và đẻ khi đang chiến đấu".
     *
     * ⚠️ "Chỉ tính thời gian đẻ" = ĐỒNG HỒ CHỈ CHẠY KHI CÓ QUÁI. Nếu đếm cả lúc rảnh thì:
     *   · giữa 2 wave, mẹ đứng không vẫn tích đủ 10 giây ⇒ vào wave là có sẵn 1 con MIỄN PHÍ;
     *   · người chơi nghỉ càng lâu càng được lợi một cách vô lý.
     * Nay: chỉ trừ đồng hồ khi trên sân CÒN QUÁI SỐNG; hết quái thì đồng hồ ĐỨNG YÊN (không
     * tích, cũng không reset — vào wave sau là đếm tiếp từ chỗ dừng).
     */
    if (cls.summonEvery) {
      const inCombat = this.enemies.length > 0;
      if (inCombat) {
        u.summonCd -= dt;
        if (u.summonCd <= 0) {
          u.summonCd = cls.summonEvery;
          this.summonMinions(u, 1, cls.summonKind ?? 'brat');
        }
      }
    }

    /*
     * ============ STREET ULTIMATE: TỰ CHẠY VÒNG QUANH NHÀ ============
     *
     * Người dùng: *"Street khi kích hoạt phải tự chạy xung quanh nhà chính để ném bom, tầm đánh khi
     * dùng ulti hết sàn đấu, nếu người chơi di chuyển thì đi theo điều khiển của người chơi, nếu
     * người dùng không điều khiển thì lại tự động"*.
     *
     * Thứ tự ưu tiên (đúng như người dùng mô tả):
     *   1. CÓ lệnh người chơi (`orderX/Y`) ⇒ nhường hoàn toàn: rơi xuống nhánh xử lý lệnh bên dưới.
     *   2. KHÔNG có lệnh ⇒ tự chạy vòng quanh Nhà theo gốc quay đều (`ultPatrolAngle`), tốc độ
     *      NHANH hơn bình thường để "chạy lung tung" đúng chất.
     * Bom vẫn được ném bởi `tickFirebomb()` (nhịp riêng của ultimate), không phụ thuộc việc đang đi.
     */
    if (this.ultActive(u) && u.kind === 'street' && u.orderX === null && u.orderY === null) {
      const ang = this.patrolAngle(u, dt);
      /*
       * ⚠️ LỖI ĐÃ SỬA (đo trên game thật): bản đầu dùng `sin(ang) * R * 0.55` và kẹp
       * `coreY + 70` ⇒ vòng bị BÓ HẸP theo trục Y, đo được quãng chạy ngang chỉ **102** đơn vị
       * trong 4 giây (trông như đứng tại chỗ). Nay:
       *   · `R` 280 — đủ rộng để đi qua nhiều vùng bom;
       *   · dùng ĐÚNG `sin(ang) * RY` với `RY = 150` (không nhân 0.55 thêm lần nữa);
       *   · kẹp theo BIÊN SÂN thật để không đứng ngoài màn khi Nhà gần mép.
       */
      const RX = 280;
      const RY = 150;
      const px = Math.max(60, Math.min(ARENA.w - 60, this.core.x + Math.cos(ang) * RX));
      const py = Math.max(ARENA.coreY + 60, Math.min(ARENA.h - 50, this.core.y + Math.sin(ang) * RY));
      u.state = 'move';
      u.facing = px >= u.x ? 1 : -1;
      this.moveToward(u, px, py, s.moveSpeed * 1.35, dt);
      return;
    }
    if (!(this.ultActive(u) && u.kind === 'street')) this.ultPatrol.clear();

    // 1) LỆNH CỦA NGƯỜI CHƠI được ưu tiên tuyệt đối: đi tới điểm đã chọn,
    //    tới nơi thì điểm đó trở thành vị trí neo mới.
    if (u.orderX !== null && u.orderY !== null) {
      const d = this.distXY(u.x, u.y, u.orderX, u.orderY);
      if (d > 14) {
        u.state = 'move';
        this.moveToward(u, u.orderX, u.orderY, s.moveSpeed * 1.2, dt);
        return;
      }
      u.homeX = u.orderX;
      u.homeY = u.orderY;
      this.heroHome.set(u.id, { x: u.orderX, y: u.orderY });
      u.orderX = null;
      u.orderY = null;
    }

    /*
     * TẦM TRUY TÌM MỤC TIÊU — BẢN SAO PHÂN THÂN ĐI TÌM QUÁI KHẮP SÂN.
     *
     * ⚠️⚠️ LỖI ĐÃ DÍNH (người dùng báo): *"Phân thân của agility không tự động di chuyển để tìm quái
     * khi mà không có quái trong tầm đánh nhưng có quái ở nơi khác"*.
     *
     * NGUYÊN NHÂN ĐO ĐƯỢC: chỗ này cũ là `pickDefenseTarget(u, 300)` cho MỌI tướng, kể cả bản sao.
     * Quái ngoài 300px bị coi như KHÔNG TỒN TẠI ⇒ bản sao rơi xuống nhánh `else`, quay về điểm neo
     * (`heroHome`) rồi đứng `idle`. Đo thật (Chrome, 12 giây liên tục):
     *   #5 idle d=466 → 363 → 392 …   (quái sống suốt, ở cách 363–589px)
     *   #3/#4 chỉ chịu đi khi quái tự bò vào gần (d=168 → chuyển `move`, d=62 → `attack`)
     * ⇒ Bản sao đứng chôn chân ở góc sân chờ quái tới, đúng hiện tượng người dùng thấy.
     *
     * VÌ SAO CHỈ NỚI CHO BẢN SAO, KHÔNG NỚI CHO TƯỚNG GỐC:
     * Tướng gốc có "dây thừng" 300px là CHỦ Ý — nó là tuyến phòng thủ quanh Nhà, người chơi còn
     * điều khiển tay bằng lệnh click. Nếu nới hết cho tướng gốc thì tướng tự bỏ vị trí phòng thủ
     * đuổi quái khắp bản đồ, phá vỡ lối chơi "kê tướng chặn cửa" mà người dùng đã chốt.
     * Bản sao thì khác: người chơi KHÔNG điều khiển được chúng (xem `startClones`), chúng được sinh
     * ra để "quẩy" khắp sân — đứng yên chờ quái là vô dụng.
     *
     * Dùng đường chéo sân làm tầm "vô hạn" thay vì `Infinity`: giữ nguyên kiểu số hữu hạn (an toàn
     * cho so sánh/ lưu save) và bao trùm toàn bộ sân với mọi vị trí đứng.
     */
    const chaseRange = u.isClone ? Math.hypot(ARENA.w, ARENA.h) : 300;
    const target = this.pickDefenseTarget(u, chaseRange);
    if (target) {
      u.targetId = target.id;
      const dist = this.dist(u, target);
      u.facing = target.x >= u.x ? 1 : -1;
      if (dist <= s.range + target.radius) {
        u.state = 'attack';
        if (u.attackCd <= 0) this.heroAttack(u, s, target);
      } else {
        u.state = 'move';
        this.moveToward(u, target.x, target.y, s.moveSpeed, dt);
      }
    } else {
      u.targetId = null;
      const home = this.heroHome.get(u.id);
      if (home && this.distXY(u.x, u.y, home.x, home.y) > 16) {
        u.state = 'move';
        this.moveToward(u, home.x, home.y, s.moveSpeed, dt);
      } else {
        u.state = 'idle';
        u.facing = 1;
      }
    }
  }

  /** Góc quay của vòng tuần tra ultimate — mỗi tướng một pha riêng để không chồng nhau. */
  private patrolAngle(u: Unit, dt: number): number {
    const cur = this.ultPatrol.get(u.id) ?? (u.id * 0.7) % (Math.PI * 2);
    const next = cur + dt * 1.1; // ~0,18 vòng/ giây ⇒ chạy vòng quanh Nhà trong ~5,7s
    this.ultPatrol.set(u.id, next);
    return next;
  }

  private updateEnemy(u: Unit, s: Stats, dt: number): void {
    const def = ENEMIES[u.kind] ?? STAGE_BOSS;
    const bossSpecial = u.isBoss ? this.bossSpecialFor(u) : 'none';

    // boss: hồi máu / triệu hồi
    if (u.isBoss) {
      if (bossSpecial === 'regen') u.hp = Math.min(u.base.hp, u.hp + u.base.hp * 0.02 * dt);
      if (bossSpecial === 'summon' && this.rng.chance(dt * 0.28)) {
        this.spawnMinionAt(u, 'thug');
      }
    }
    if (def.behavior === 'spawner' && def.spawnEvery) {
      u.summonCd -= dt;
      if (u.summonCd <= 0) {
        u.summonCd = def.spawnEvery;
        this.spawnMinionAt(u, def.spawnKind ?? 'thug');
      }
    }

    // ưu tiên: tướng trong tầm → tướng gần nhất trong aggro → Nhà
    const hero = this.pickNearestHero(u, def.behavior === 'ranged' ? 260 : 200);
    if (hero) {
      u.targetId = hero.id;
      u.facing = hero.x >= u.x ? 1 : -1;
      const dist = this.dist(u, hero);
      const want = def.behavior === 'ranged' ? (def.keepDistance ?? 170) : s.range;
      if (dist <= s.range + hero.radius && def.behavior !== 'ranged') {
        u.state = 'attack';
        if (u.attackCd <= 0) this.enemyAttack(u, s, hero);
      } else if (def.behavior === 'ranged' && Math.abs(dist - want) < 40) {
        u.state = 'attack';
        if (u.attackCd <= 0) this.enemyAttack(u, s, hero);
      } else {
        u.state = 'move';
        if (def.behavior === 'ranged' && dist < want) {
          // lùi ra xa để giữ khoảng cách
          const dx = u.x - hero.x;
          const dy = u.y - hero.y;
          const len = Math.hypot(dx, dy) || 1;
          this.moveDir(u, dx / len, dy / len, s.moveSpeed * 0.8, dt);
        } else {
          this.moveToward(u, hero.x, hero.y, s.moveSpeed, dt);
        }
      }
      return;
    }

    // không có tướng trong tầm ⇒ đánh Nhà
    u.targetId = null;
    const dc = this.distXY(u.x, u.y, this.core.x, this.core.y);
    u.facing = this.core.x >= u.x ? 1 : -1;
    if (dc <= s.range + this.core.radius) {
      u.state = 'attack';
      if (u.attackCd <= 0) this.enemyAttackCore(u, s);
    } else {
      u.state = 'move';
      this.moveToward(u, this.core.x, this.core.y, s.moveSpeed, dt);
    }
  }

  private bossSpecialFor(u: Unit): string {
    const affixes = BOSS_AFFIXES;
    return affixes[(u.tier - 1) % affixes.length]?.special ?? 'none';
  }

  // ------------------------------------------------------------ tấn công

  private heroAttack(u: Unit, s: Stats, target: Unit): void {
    const cls = CLASSES[u.kind as ClassId];
    u.attackCd = 1 / Math.max(0.15, s.atkSpeed);
    u.swing = 1;

    // Quân đội: tốn đạn
    let atkMult = 1;
    if (cls.usesAmmo) {
      if (u.ammo > 0) {
        u.ammo -= 1;
      } else {
        atkMult = 0.25; // hết đạn ⇒ chỉ còn cận chiến yếu
      }
    }

    if (cls.ranged) {
      /*
       * `skin` = hình RIÊNG của class. Người dùng: *"kích cỡ đường đạn đang to quá và đang giống
       * nhau, cần custom hình ảnh lại cho đúng với nhân vật"* ⇒ mỗi class/ chiêu một hình khác.
       *
       * ⚠️⚠️ LUẬT "CLASS NÀO ĐÁNH XA" LẤY TỪ DỮ LIỆU (`ClassDef.ranged`), KHÔNG hard-code tên class
       * ở đây. Bản cũ viết `cls.id === 'army' || cls.id === 'street'` và tầng Arena chép lại y hệt
       * (`basicShotSkin`) ⇒ thêm một class đánh xa (Đẻ mướn, 02/10/2026) là phải sửa 2 chỗ, quên 1 chỗ
       * là hai chế độ lệch nhau. Nay cả hai đọc đúng một trường.
       */
      this.spawnProjectile(u, target, s.atk * atkMult, cls.ranged.kind, cls.ranged.skin);
      return;
    }
    /*
     * DÃ TRƯ: ĐÁNH THƯỜNG BIẾN THÀNH TIẾNG GẦM AOE gây choáng.
     * Người dùng chốt: *"đánh thường biến thành tiếng gầm AOE dame và gây choáng"* — nên khi
     * ultimate đang bật thì BỎ đòn đánh đơn, thay bằng gầm quét cả vùng.
     */
    if (this.ultActive(u) && cls.id === 'strength') {
      this.boarRoar(u, this.units);
      return;
    }
    // CẬN CHIẾN RA ĐÒN VÕ: mỗi đòn đổi một thế (đấm → móc → đá → chỏ → chưởng)
    u.attackMove = (u.attackMove + 1) % 97;
    const crit = this.rng.chance(s.crit);
    const res = computeDamage({
      atk: s.atk * atkMult,
      multiplier: crit ? 1.8 : 1,
      def: effectiveStats(target).def,
      targetMaxHp: target.base.hp,
      isEliteOrBoss: target.isElite || target.isBoss,
    });
    this.applyDamage(target, res.damage, u, crit, res.floored);
  }

  private enemyAttack(u: Unit, s: Stats, target: Unit): void {
    const def = ENEMIES[u.kind] ?? STAGE_BOSS;
    u.attackCd = 1 / Math.max(0.15, s.atkSpeed);
    u.swing = 1;
    // CUỒNG NỘ: wave kéo dài quá lâu thì quái mạnh dần
    const rage = enrageMultiplier(this.time - this.waveStartedAt);
    if (def.behavior === 'ranged') {
      this.spawnProjectile(u, target, s.atk * rage, def.projectile ?? 'thrown');
      return;
    }
    u.attackMove = (u.attackMove + 1) % 97;
    const ts = effectiveStats(target);
    const crit = this.rng.chance(s.crit);
    const res = computeDamage({
      atk: s.atk * rage,
      multiplier: crit ? 1.6 : 1,
      def: ts.def,
      targetMaxHp: target.base.hp,
      isEliteOrBoss: u.isBoss || u.isElite,
    });
    this.applyDamage(target, res.damage, u, crit, res.floored);
  }

  private enemyAttackCore(u: Unit, s: Stats): void {
    u.attackCd = 1 / Math.max(0.15, s.atkSpeed);
    u.swing = 1;
    const rage = enrageMultiplier(this.time - this.waveStartedAt);
    let dmg = s.atk * 0.6 * rage;
    if (this.core.shield > 0) {
      const absorbed = Math.min(this.core.shield, dmg);
      this.core.shield -= absorbed;
      dmg -= absorbed;
    }
    this.core.hp = Math.max(0, this.core.hp - dmg);
    this.stats.coreDamage += dmg;
    this.burst({ x: this.core.x + this.rng.range(-30, 30), y: this.core.y + this.rng.range(-24, 24), radius: 30, color: 0xf87171, ttl: 0.3, maxTtl: 0.3, kind: 'hit' });
    // Nhà bị đập: bụi + mảnh vụn + vệt nứt lan trên nền ⇒ người chơi THẤY mình đang thua
    this.burst({ x: this.core.x, y: this.core.y + 26, radius: 46, color: 0xd6c4a8, ttl: 0.55, maxTtl: 0.55, kind: 'dust' });
    this.burst({ x: this.core.x, y: this.core.y + 30, radius: 60, color: 0xb45309, ttl: 0.7, maxTtl: 0.7, kind: 'crack' });
    this.burst({ x: this.core.x, y: this.core.y - 10, radius: 34, color: 0xfb7185, ttl: 0.34, maxTtl: 0.34, kind: 'ring', scale: 0.9 });
    this.shockPower = Math.max(this.shockPower, 0.3);
    this.events.push({ type: 'coreHit', x: this.core.x, y: this.core.y });
    if (this.core.hp <= 0) this.lose();
  }

  private spawnProjectile(from: Unit, target: Unit, atk: number, kind: Projectile['kind'], skin?: string): void {
    const dx = target.x - from.x;
    const dy = target.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    /*
     * ⚠️ TỐC ĐỘ ĐẠN QUYẾT ĐỊNH "CÓ NHÌN THẤY ĐƯỜNG ĐẠN HAY KHÔNG".
     *
     * Người dùng báo: *"tôi thử dân chợ búa chỉ thấy xuất hiện 1 cục màu xanh ở thân và không thấy
     * gì bay đi cả"*. Đo được: đạn ném bay 520px/s nhưng quãng đường thực chỉ ~110px (từ nòng súng
     * tới mép tầm bắn `range 195 + radius quái 17 − 32`) ⇒ sống **1–9 khung (25–225ms)** rồi biến
     * mất. Ở 60fps mắt chỉ kịp thấy 1–9 khung ⇒ đúng cảm giác "một cục xanh loé lên rồi biến mất".
     *
     * Nay: đạn ném 380px/s (chậm hơn nhưng vẫn "bay"), đạn súng 620px/s. Quãng 110px ⇒ ~290ms với
     * đồ ném (≈17 khung) — đủ để mắt bám theo đường bay.
     */
    /*
     * ĐO ĐƯỢC (Chrome thật, Dân chợ búa): tầm bắn 195 + radius quái 17 = 212px, nhưng tướng chỉ
     * bắt đầu bắn khi quái vào tầm ⇒ quãng bay thực tế chỉ ~110–150px. Với tốc độ cũ 520px/s thì
     * đạn sống **4–9 khung (65–150ms)** — người dùng chỉ thấy "1 cục xanh ở thân rồi biến mất".
     * Nay đồ ném 300px/s ⇒ quãng 110–150px là **370–500ms (22–30 khung)**: mắt bám theo được.
     * Đạn súng 620px/s (tầm army 265 nên bay xa hơn, vẫn kịp thấy).
     */
    const speed = kind === 'bullet' ? 620 : 300;
    this.projectiles.push({
      id: nextId++,
      team: from.team,
      x: from.x,
      y: from.y - 14,
      vx: (dx / len) * speed,
      vy: (dy / len) * speed,
      damage: atk,
      ownerId: from.id,
      ttl: 2.2,
      radius: kind === 'bullet' ? 5 : 8,
      color: kind === 'bullet' ? 0xfde68a : 0x86efac,
      kind,
      skin,
    });
    this.stats.shots++;
    // LỬA ĐẦU NÒNG: vệt sáng ngắn theo hướng bắn ⇒ thấy ngay ai vừa nổ súng
    const a = Math.atan2(dy, dx);
    this.burst({ x: from.x + Math.cos(a) * 12, y: from.y - 14 + Math.sin(a) * 12, radius: 14, color: kind === 'bullet' ? 0xfde68a : 0x86efac, ttl: 0.16, maxTtl: 0.16, kind: 'streak', angle: a, len: 40 });
    this.burst({ x: from.x + Math.cos(a) * 16, y: from.y - 14 + Math.sin(a) * 16, radius: 10, color: 0xffffff, ttl: 0.12, maxTtl: 0.12, kind: 'flash', scale: 0.35 });
  }

  private applyDamage(target: Unit, damage: number, from: Unit, crit: boolean, floored: boolean): void {
    if (isInvulnerable(target, this.time)) {
      this.pushFloat(target.x, target.y - 30, 'NÉ!', 0x60a5fa, 'dodge');
      return;
    }
    const ts = effectiveStats(target);
    if (target.team === 'hero' && this.rng.chance(ts.dodge)) {
      this.pushFloat(target.x, target.y - 30, 'NÉ', 0x60a5fa, 'dodge');
      return;
    }

    let dmg = damage;
    if (target.shield > 0) {
      const absorbed = Math.min(target.shield, dmg);
      target.shield -= absorbed;
      dmg -= absorbed;
      if (absorbed > 0) this.pushFloat(target.x, target.y - 30, `-${Math.round(absorbed)}`, 0x38bdf8, 'damage');
    }

    // ĐÒN BỊ CHẶN HOÀN TOÀN — đây chính là cảm giác "vượt trội" ở wave đầu
    if (dmg <= 0) {
      if (target.team === 'hero' && floored) {
        this.stats.blocked += 1;
        if (this.rng.chance(0.25)) this.pushFloat(target.x, target.y - 40, 'CHẶN!', 0xfacc15, 'dodge', 0.8);
      }
      return;
    }

    target.hp -= dmg;
    target.hitFlash = 1;

    if (target.team === 'enemy') {
      this.stats.damageDealt += dmg;
      if (floored) this.stats.damageFloored += 1;
      this.pushFloat(target.x, target.y - 34, `${Math.round(dmg)}`, crit ? 0xfbbf24 : 0xf8fafc, crit ? 'crit' : 'damage');
      // phản đòn của boss
      if (target.isBoss && this.bossSpecialFor(target) === 'reflect' && from.team === 'hero') {
        const back = dmg * 0.25;
        from.hp -= back;
        this.pushFloat(from.x, from.y - 30, `-${Math.round(back)}`, 0xf472b6, 'damage');
        if (from.hp <= 0) this.killUnit(from);
      }
    } else {
      this.stats.damageTaken += dmg;
      this.pushFloat(target.x, target.y - 34, `-${Math.round(dmg)}`, 0xfb7185, 'damage');
      this.events.push({ type: 'hit', x: target.x, y: target.y });
    }
    /*
     * VA CHẠM "CÓ HƯỚNG": tia bắn theo đúng hướng người đánh ⇒ mắt đọc ra đòn từ đâu tới.
     * `power` lấy theo tỉ lệ máu mất của nạn nhân (đòn nặng ⇒ hiệu ứng to, khựng lâu hơn).
     */
    const power = Math.max(0.1, Math.min(1, dmg / Math.max(1, target.base.hp) * 1.6));
    const ang = this.angleBetween(from.x, from.y, target.x, target.y);
    const hitColor = target.team === 'enemy' ? (crit ? 0xfbbf24 : 0xfca5a5) : 0xfb7185;
    this.hitBursts(target.x, target.y - 12, ang, hitColor, power, crit);

    if (target.hp <= 0) this.killUnit(target);
  }

  private killUnit(u: Unit): void {
    if (!u.alive) return;
    u.alive = false;
    u.state = 'dead';
    u.deadAt = this.time;
    u.hp = 0;
    u.buffs = [];
    const dieColor = u.team === 'enemy' ? 0xf97316 : 0x60a5fa;
    this.burst({ x: u.x, y: u.y, radius: u.radius * 2.6, color: dieColor, ttl: 0.5, maxTtl: 0.5, kind: 'die' });
    // XÁC NỔ: bụi + mảnh vụn + vòng xung kích ⇒ cái chết "nặng" hơn hẳn
    this.burst({ x: u.x, y: u.y, radius: u.radius * 2.4, color: dieColor, ttl: 0.42, maxTtl: 0.42, kind: 'ring', scale: 0.8 });
    const dN = u.isBoss ? 14 : u.isElite ? 8 : 4;
    for (let i = 0; i < dN; i++) {
      const a = (i / dN) * Math.PI * 2 + this.fxRng.range(-0.3, 0.3);
      this.burst({
        x: u.x + Math.cos(a) * u.radius * 0.4, y: u.y + Math.sin(a) * u.radius * 0.3,
        radius: 5 + u.radius * 0.18, color: dieColor,
        ttl: 0.45 + this.fxRng.range(0, 0.25), maxTtl: 0.6, kind: 'spark', angle: a, len: 26 + u.radius * 1.4,
      });
    }
    this.burst({ x: u.x, y: u.y, radius: u.radius * 1.8, color: 0xcbb79a, ttl: 0.7, maxTtl: 0.7, kind: 'dust', scale: 1.1 });
    if (u.isBoss || u.isElite) {
      this.burst({ x: u.x, y: u.y, radius: u.radius * 3, color: 0xffffff, ttl: 0.22, maxTtl: 0.22, kind: 'flash', scale: 0.9 });
      this.hitStop = Math.max(this.hitStop, 0.09);
      this.shockPower = Math.max(this.shockPower, 0.7);
    }
    if (u.team === 'enemy') {
      const def = ENEMIES[u.kind] ?? STAGE_BOSS;
      const g = enemyGold(this.wave, u.isBoss, u.isElite) * (def.goldMult ?? 1);
      this.gold += g;
      this.stats.kills += 1;
      this.pushFloat(u.x, u.y - 46, `+${Math.round(g)}`, 0xfbbf24, 'gold');
    }
    this.events.push({ type: 'die', x: u.x, y: u.y, power: u.team === 'enemy' ? 1 : 0 });
  }

  // ------------------------------------------------------------ skill

  /**
   * TẦM ĐỂ AUTO DÙNG CHIÊU — suy từ CHÍNH CHIÊU, không dùng một con số chung.
   *
   * ⚠️ Vì sao không dùng `max(160, s.range + 90)` như trước: chiêu cận chiến ăn theo
   * BÁN KÍNH NỔ quanh người (Địa Chấn Cước 150px, Chợ Vỡ 300px), còn chiêu ném/ bắn ăn
   * theo TẦM NÉM. Lấy một con số chung thì hero đứng cách quái 250px vẫn "niệm" Địa Chấn
   * Cước (nổ vào không khí) rồi hết hồi; hoặc ngược lại, quái ngay sát mà không dùng vì
   * tầm chung quá ngắn.
   *
   * `reach <= 0` = chiêu KHÔNG cần mục tiêu trong tầm (hồi máu, triệu hồi).
   */
  private skillReach(sk: SkillDef, s: Stats): number {
    switch (sk.kind) {
      // nổ/ khống chế quanh người ⇒ phải có quái trong BÁN KÍNH đó
      case 'aoeDamage':
      case 'debuff':
        return sk.radius ?? 120;
      // lao tới đánh ⇒ tầm lao
      case 'dashStrike':
        return sk.distance ?? 220;
      // ném/ bắn ⇒ tầm ném (khai ở data; mặc định lấy tầm đánh thường, tối thiểu 320)
      case 'projectile':
        return sk.range ?? Math.max(s.range, 320);
      // không cần mục tiêu
      case 'heal':
      case 'summon':
        return 0;
      /*
       * buff/ khiên/ bất tử: dùng khi ĐANG giao chiến.
       * ⚠️ ĐO ĐƯỢC: để `s.range + 80` thì lời hứa "wave 1 tank mất dưới 5% máu" vỡ (đo 40
       * seed: tệ nhất còn 93,8%) vì buff phòng thủ nổ muộn hơn bản cũ (`max(160, range+90)`
       * = 156 với tank range 66). Chiêu PHÒNG THỦ không gây sát thương nên không cần "có
       * quái trong tầm đánh" — cho nổ sớm hơn: `range + 110` = 176, rộng hơn bản cũ một chút.
       */
      default:
        return s.range + 110;
    }
  }

  /** Chiêu hồi máu có đáng dùng không (Bắn thuê hồi ĐẠN, class khác hồi MÁU). */
  private healWanted(u: Unit, s: Stats): boolean {
    if (CLASSES[u.kind as ClassId]?.usesAmmo) return u.ammo < 30;
    return u.hp < s.hp * 0.75;
  }

  /**
   * AUTO NIỆM CHIÊU — CHỌN CHIÊU TỐT NHẤT, không xả hết.
   *
   * ⚠️ YÊU CẦU NGƯỜI DÙNG: "Skill tự động dùng phải thông minh để biết khi nào nên chọn
   * skill nào để đánh khi nhiều skill đang có thể cast".
   *
   * Bản cũ duyệt tuần tự và NIỆM HẾT mọi chiêu đủ điều kiện trong cùng một tick ⇒ vừa
   * phí mana (dùng tuyệt chiêu 90 mana để giết 1 con thú) vừa "máy móc".
   *
   * Bản mới: chấm điểm từng chiêu rồi chỉ niệm MỘT chiêu tốt nhất, cách nhau `AUTO_CAST_GAP`
   * giây. Điểm = LỢI ÍCH / MANA (lợi ích ước từ số quái trong tầm, có thưởng khi có boss),
   * nên bot tự biết: đông thì dùng chiêu diện rộng, có boss thì dùng chiêu mạnh, máu thấp
   * thì ưu tiên khiên/ hồi, còn lại thì dùng chiêu rẻ.
   */
  private tryAutoCast(u: Unit, s: Stats): void {
    if (this.enemies.length === 0) return;
    if ((u.autoCastCd ?? 0) > this.time) return;
    const skills = this.skillsFor(u.kind);
    /*
     * ⚠️ BÀI HỌC ĐO ĐƯỢC: nếu để tiện ích (khiên/ buff/ bất tử/ hồi) TRANH ĐIỂM với chiêu
     * sát thương thì bot gần như không bao giờ dùng chúng (sát thương luôn điểm cao hơn)
     * ⇒ tướng mất khả năng sống sót, tỉ lệ qua màn sụp 95% → 12,5% (đo bằng `npm run balance`).
     * Cách đúng: TIỆN ÍCH XÉT TRƯỚC (chúng không tranh lượt với chiêu đánh), chỉ khi không
     * cần tiện ích mới chọn chiêu sát thương TỐT NHẤT.
     */
    let bestIdx = this.pickUtilitySkill(u, s, skills);
    if (bestIdx < 0) bestIdx = this.pickDamageSkill(u, s, skills);
    if (bestIdx < 0) return;
    if (this.castSkill(u, bestIdx, true)) {
      u.skillCds[bestIdx] = this.time + skills[bestIdx].cooldown;
      u.autoCastCd = this.time + AUTO_CAST_GAP;
    }
  }

  /** Đang có buff này chưa (tên buff = id chiêu) — dùng để KHÔNG niệm lại khi còn hiệu lực. */
  private hasBuff(u: Unit, id: string): boolean {
    return u.buffs.some((b) => b.id === id && b.until > this.time);
  }

  /** Chiêu TIỆN ÍCH: hồi máu/ đạn · khiên · bất tử · buff · triệu hồi. */
  private static readonly UTILITY_KINDS: ReadonlyArray<SkillDef['kind']> = ['heal', 'shield', 'invuln', 'buff', 'summon'];

  private pickUtilitySkill(u: Unit, s: Stats, skills: SkillDef[]): number {
    let best = -1;
    let bestValue = 0;
    for (let i = 0; i < skills.length; i++) {
      const sk = skills[i];
      if (!World.UTILITY_KINDS.includes(sk.kind)) continue;
      if (this.wave < sk.unlockWave) continue;
      if (u.mana < sk.manaCost) continue;
      if ((u.skillCds[i] ?? 0) > this.time) continue;
      // không niệm lại buff đang còn hiệu lực (tiết kiệm mana — đây cũng là "thông minh")
      if (sk.kind === 'buff' && this.hasBuff(u, sk.id)) continue;
      if (sk.kind === 'shield' && u.shield > 0) continue;
      if (sk.kind === 'invuln' && isInvulnerable(u, this.time)) continue;
      const value = this.scoreSkill(u, s, sk);
      if (value > bestValue) {
        bestValue = value;
        best = i;
      }
    }
    return best;
  }

  private pickDamageSkill(u: Unit, s: Stats, skills: SkillDef[]): number {
    let best = -1;
    let bestValue = 0;
    for (let i = 0; i < skills.length; i++) {
      const sk = skills[i];
      if (World.UTILITY_KINDS.includes(sk.kind)) continue;
      if (this.wave < sk.unlockWave) continue;
      if (u.mana < sk.manaCost) continue;
      if ((u.skillCds[i] ?? 0) > this.time) continue;
      const value = this.scoreSkill(u, s, sk);
      if (value > bestValue) {
        bestValue = value;
        best = i;
      }
    }
    return best;
  }

  /**
   * CHẤM ĐIỂM 1 CHIÊU cho bot — càng cao càng đáng dùng.
   * Trả `<= 0` nghĩa là "đừng dùng" (không có mục tiêu trong tầm, hoặc chưa cần).
   */
  private scoreSkill(u: Unit, s: Stats, sk: SkillDef): number {
    const reach = this.skillReach(sk, s);
    const inReach = reach > 0 ? this.enemiesNear(u.x, u.y, reach) : [];
    const hpPct = u.hp / Math.max(1, s.hp);
    const hasBoss = inReach.some((e) => e.isBoss);
    const hasElite = inReach.some((e) => e.isElite);
    // LỢI ÍCH thô của chiêu (sát thương/ ích lợi ước lượng)
    let gain: number;
    switch (sk.kind) {
      case 'aoeDamage': {
        if (inReach.length === 0) return -1;
        // diện rộng: ăn theo SỐ QUÁI TRÚNG (tối đa 6 cho khỏi lệch thang điểm)
        gain = s.atk * (sk.mult ?? 1) * Math.min(inReach.length, 6);
        break;
      }
      case 'projectile': {
        if (inReach.length === 0) return -1;
        const hits = Math.min(inReach.length, sk.count ?? 1);
        gain = s.atk * (sk.mult ?? 1) * hits;
        break;
      }
      case 'dashStrike': {
        if (inReach.length === 0) return -1;
        gain = s.atk * (sk.mult ?? 1) * (hasBoss || hasElite ? 1.8 : 1.15);
        break;
      }
      case 'debuff': {
        if (inReach.length === 0) return -1;
        // khống chế không gây sát thương ⇒ quy ra "đỡ được bao nhiêu đòn"
        gain = s.atk * 0.9 * Math.min(inReach.length, 5);
        break;
      }
      case 'heal': {
        // Bắn thuê hồi ĐẠN, class khác hồi MÁU — xem `healWanted`
        if (!this.healWanted(u, s)) return -1;
        gain = CLASSES[u.kind as ClassId]?.usesAmmo ? 260 : hpPct < 0.35 ? 1400 : 420;
        break;
      }
      case 'shield': {
        if (inReach.length === 0) return -1;
        gain = hpPct < 0.6 ? 950 : 260;
        break;
      }
      case 'invuln': {
        if (inReach.length === 0 && hpPct > 0.5) return -1;
        // bất tử: đáng giá nhất khi đang bị boss/ đông quái ép
        gain = (hasBoss ? 1100 : 0) + (hpPct < 0.4 ? 900 : 0) + (inReach.length >= 3 ? 350 : 120);
        break;
      }
      case 'buff': {
        if (inReach.length === 0) return -1;
        gain = sk.stat === 'atk' || sk.stat === 'atkSpeed' ? 420 : 300;
        break;
      }
      case 'summon': {
        // chỉ gọi quân khi còn chỗ và đang có quái trên sân
        if (this.minionCount() >= MAX_MINIONS_TOTAL) return -1;
        gain = 380;
        break;
      }
      default:
        return -1;
    }
    // CÓ BOSS ⇒ ưu tiên chiêu mạnh; đông quái ⇒ ưu tiên diện rộng (đã tính ở `gain`)
    if (hasBoss) gain *= 1.5;
    else if (hasElite) gain *= 1.2;
    // CHIA THEO MANA: cùng lợi ích thì chiêu rẻ hơn thắng ⇒ không đốt tuyệt chiêu vào 1 con
    return gain / Math.max(1, sk.manaCost);
  }

  /** Số đệ tử đang sống (mọi loại) — dùng cho trần 15 con. */
  minionCount(): number {
    /*
     * ⚠️ QUÂN THU PHỤC KHÔNG TÍNH VÀO TRẦN 15 CON ĐẺ.
     * Người dùng chốt: *"Số lượng enemy thu phục tuyệt đối không tính vào số con đẻ ra"*.
     * `isConverted` được lọc ở đây; quân thu phục cũng có `minions = 0` (xem `convertToAlly`).
     */
    let n = 0;
    for (const u of this.units) if (u.alive && u.team === 'hero' && u.minions > 0 && !u.isConverted) n++;
    return n;
  }

  // ================================================================ ULTIMATE
  /*
   * 5 ULTIMATE ĐIỀU KHIỂN THỦ CÔNG — người dùng yêu cầu riêng cho từng class, hồi **3 phút**.
   * Khác hẳn 7 chiêu thường: người chơi CHỦ ĐỘNG bấm (nút hiện khi chọn tướng), hiệu lực kéo dài
   * theo giây, và mỗi class có luật riêng (xem `data/ultimates.ts` cho phần chữ hiển thị).
   *
   * Hằng số ở đây là NGUỒN SỰ THẬT cho luật; `data/ultimates.ts` chỉ mô tả chữ.
   */
  static readonly ULT_COOLDOWN = 180;

  /** Thời gian duy trì (giây) của ultimate theo class. `Infinity` = tới khi vật chết. */
  static readonly ULT_DURATION: Record<string, number> = {
    strength: 15,
    agility: 20,
    street: 20,
    army: 20,
    mother: Number.POSITIVE_INFINITY,
  };

  /** Mốc hồi giữa 2 lần NUỐT của Dã trư (giây). */
  private static readonly DEVOUR_INTERVAL = 2;

  /**
   * BÁN KÍNH TIẾNG GẦM AOE của Dã trư (đơn vị toạ độ sân).
   *
   * NGUỒN SỰ THẬT DUY NHẤT cho vùng sát thương của đòn đánh thường khi hoá Dã trư:
   * · `boarRoar()` dùng làm bán kính quét sát thương/ choáng.
   * · `startGiantBoar()` đặt `range` bằng CHÍNH giá trị này ⇒ "tầm đánh" khớp vùng sát thương
   *   thật, không còn cảnh phải bò sát mới gầm được (task-5).
   *
   * ⚠️ Sửa số ở đây là sửa cả hai chỗ cùng lúc — đúng ý đồ. ĐỪNG viết lại số 120 ở nơi khác.
   */
  static readonly BOAR_ROAR_RADIUS = 120;

  /** Nhịp ném bom xăng của Dân chợ búa (giây). */
  private static readonly FIREBOMB_INTERVAL = 0.75;
  /** Nhịp thả bom của máy bay (giây). */
  private static readonly AIRSTRIKE_INTERVAL = 0.5;

  /**
   * VÒNG CẬP NHẬT ULTIMATE — chạy mỗi nhịp mô phỏng.
   *
   * Gồm 2 việc:
   *   1. HẾT HIỆU LỰC ⇒ dọn dẹp (quân thu phục TRẢ VỀ làm quái).
   *   2. NHỊP RIÊNG của từng ultimate: nuốt (strength), ném bom xăng (street), thả bom (army).
   *      Phân thân (agility) không cần nhịp riêng — bản sao là unit bình thường, tự đánh.
   */
  private updateUltimates(dt: number): void {
    for (const u of this.units) {
      if (!u.alive) continue;

      // ---- 1) HẾT HIỆU LỰC
      if (u.ultUntil > 0 && u.ultUntil <= this.time) {
        u.ultUntil = 0;
        const st = this.ultState.get(u.id);
        this.ultState.delete(u.id);
        void st;
        if (u.kind === 'agility') this.despawnClones(u);
        /*
         * THU PHỤC: người dùng chốt *"Khi enemy hết hiệu lực hoặc không còn quái trên sân (khi kết
         * thúc màn chơi), sẽ trở lại làm quái"*. Đẻ mướn có thời hạn VÔ HẠN nên nhánh này chỉ chạy
         * khi hết màn (xem `revertConverted` được gọi ở `onWaveClear`/ `finish`).
         */
        if (u.kind === 'mother') this.revertConverted();
      }
      if (!this.ultActive(u)) continue;

      // ---- 2) NHỊP RIÊNG
      switch (u.kind) {
        case 'strength':
          this.tickDevour(u, dt);
          break;
        case 'street':
          this.tickFirebomb(u, dt);
          break;
        case 'army':
          this.tickAirstrike(u, dt);
          break;
        default:
          break;
      }
    }
  }

  /**
   * DÃ TRƯ — mỗi 2s NUỐT 1 enemy ĐANG BỊ NHẮM.
   *
   * Người dùng chốt: *"Mỗi 2s sẽ nuốt và tiêu diệt 1 enemy… Không thể nuốt boss"* và khi tôi hỏi
   * nuốt con nào: *"nuốt đối tượng mà strength đang forcus đánh"* ⇒ dùng `u.targetId`, KHÔNG tự
   * chọn con gần nhất.
   */
  private tickDevour(u: Unit, dt: number): void {
    const st = this.ultState.get(u.id);
    if (!st) return;
    if (this.time < st.devourAt) return;
    st.devourAt = this.time + World.DEVOUR_INTERVAL;
    const target = u.targetId !== null ? this.units.find((x) => x.id === u.targetId) : undefined;
    // không có mục tiêu, hoặc mục tiêu là BOSS ⇒ nhịn (đúng luật "không thể nuốt boss")
    if (!target || !target.alive || target.team !== 'enemy' || target.isBoss) return;
    this.devour(u, target);
  }

  /** Nuốt trọn 1 đơn vị: xoá khỏi sân + hồi máu cho kẻ nuốt + hiệu ứng. */
  private devour(eater: Unit, victim: Unit): void {
    const heal = Math.round(victim.base.hp * 0.5);
    eater.hp = Math.min(eater.base.hp * 2.5, eater.hp + heal);
    victim.hp = 0;
    victim.alive = false;
    victim.state = 'dead';
    victim.deadAt = this.time;
    this.pushFloat(eater.x, eater.y - 46, 'NUỐT!', 0xfbbf24, 'crit', 1.6);
    this.pushFloat(victim.x, victim.y - 30, `+${heal}`, 0x4ade80, 'heal', 1);
    this.burst({ x: victim.x, y: victim.y, radius: 44, color: 0xfbbf24, ttl: 0.5, maxTtl: 0.5, kind: 'impact' });
    // nuốt cũng được tính là hạ gục (thống kê trận đấu)
    this.stats.kills++;
  }

  /**
   * DÂN CHỢ BÚA — chạy lung tung ném bom xăng liên tục vào VÙNG có enemy.
   *
   * Người dùng chốt: *"Chạy lung tung ném bom xăng liên tục vào các vùng có enemy (ném mỗi vùng
   * ảnh hưởng 1 bom rồi ném bên khác, ưu tiên vùng nhiều quái nhất), thiêu đốt và giảm giáp kẻ
   * địch"* ⇒ chia sân thành các vùng dọc, đếm quái mỗi vùng, ném vào vùng ĐÔNG NHẤT **khác** vùng
   * vừa ném (`lastBombZone`).
   */
  private tickFirebomb(u: Unit, dt: number): void {
    const st = this.ultState.get(u.id);
    if (!st) return;
    if (this.time < st.bombAt) return;
    st.bombAt = this.time + World.FIREBOMB_INTERVAL;

    const ZONES = 5;
    const zoneW = ARENA.w / ZONES;
    const count = new Array<number>(ZONES).fill(0);
    const sumX = new Array<number>(ZONES).fill(0);
    const sumY = new Array<number>(ZONES).fill(0);
    for (const e of this.units) {
      if (!e.alive || e.team !== 'enemy') continue;
      const z = Math.max(0, Math.min(ZONES - 1, Math.floor(e.x / zoneW)));
      count[z]++;
      sumX[z] += e.x;
      sumY[z] += e.y;
    }
    // chọn vùng ĐÔNG NHẤT, ưu tiên vùng KHÁC vùng vừa ném
    let best = -1;
    let bestN = 0;
    for (let z = 0; z < ZONES; z++) {
      if (count[z] === 0) continue;
      const penalty = z === this.lastBombZone ? 0.5 : 1;
      if (count[z] * penalty > bestN) {
        bestN = count[z] * penalty;
        best = z;
      }
    }
    if (best < 0) return; // không còn quái ⇒ không ném
    this.lastBombZone = best;
    const tx = sumX[best] / count[best];
    const ty = sumY[best] / count[best];
    this.firebombAt(u, tx, ty);
  }

  /**
   * NÉM CHAI RƯỢU theo ĐƯỜNG VÒNG CUNG rồi nổ ở CUỐI CUNG.
   *
   * Người dùng: *"tôi không thấy ném chai rượu, tôi muốn khi dùng skill đó street sẽ ném chai rượu
   * bay theo đường vòng cung vào 1 khu vực và gây dame aoe trong khu vực đó, có hiệu ứng cháy phạm
   * vi ảnh hưởng"*.
   *
   * ⚠️ LỖI CŨ: `firebombAt()` gây sát thương NGAY và chỉ vẽ 1 burst ở giữa đường — người chơi không
   * thấy chai bay, không thấy cung, nổ xảy ra tức thì.
   * Nay: tạo PROJECTILE thật bay parabol (vận tốc ngang + `arcHeight` cho lớp vẽ), đặt `explodeAt`
   * = lúc tới đích, và chỉ khi tới nơi mới gọi `firebombExplode()` (sát thương + vùng cháy).
   */
  private firebombAt(from: Unit, tx: number, ty: number): void {
    const dist = Math.max(1, this.distXY(from.x, from.y, tx, ty));
    const SPEED = 420; // đơn vị/ giây — đủ chậm để NHÌN THẤY cung bay
    const flight = Math.max(0.28, dist / SPEED);
    this.projectiles.push({
      id: nextId++,
      team: from.team,
      x: from.x,
      y: from.y - 20, // rời TAY chứ không rời chân
      vx: ((tx - from.x) / flight) * 1,
      vy: (ty - from.y) / flight,
      damage: from.base.atk * 1.2,
      ownerId: from.id,
      ttl: flight,
      radius: 10,
      color: 0x86efac,
      kind: 'thrown',
      skin: 'xebom-bottle',
      arcHeight: 46 + dist * 0.22, // cung cao dần theo khoảng ném
      arcT: 0,
      flightTotal: flight,
      explodeAt: this.time + flight,
      fireRadius: World.FIREBOMB_RADIUS,
    });
    this.pushFloat(from.x, from.y - 40, 'NÉM!', 0xf97316, 'dodge', 0.6);
  }

  /** Bán kính vùng cháy của chai rượu (nguồn số duy nhất — dùng cả cho sát thương lẫn hiệu ứng). */
  static readonly FIREBOMB_RADIUS = 88;
  /** Thời gian vùng cháy còn hiệu lực (giây). */
  static readonly FIRE_ZONE_TTL = 4;

  /**
   * CHAI RƯỢU NỔ: sát thương AOE + THIÊU ĐỐT + GIẢM GIÁP + ĐỂ LẠI VÙNG CHÁY.
   *
   * Người dùng: *"gây dame aoe trong khu vực đó, có hiệu ứng cháy phạm vi ảnh hưởng"*.
   * Vùng cháy (`fireZones`) là dữ liệu của sim ⇒ enemy đi vào SAU khi nổ vẫn bị thiêu đốt, và lớp
   * hiệu ứng đọc chính danh sách này để vẽ vòng lửa (không vẽ ở client rồi quên đồng bộ).
   */
  private firebombExplode(p: Projectile): void {
    const from = this.units.find((u) => u.id === p.ownerId);
    const R = p.fireRadius ?? World.FIREBOMB_RADIUS;
    this.fireZones.push({ x: p.x, y: p.y, radius: R, until: this.time + World.FIRE_ZONE_TTL, ownerId: p.ownerId });
    // hiệu ứng cháy phạm vi ảnh hưởng (vòng lửa) — dùng burst 'ring' để thấy rõ bán kính
    this.burst({ x: p.x, y: p.y, radius: R, color: 0xf97316, ttl: 0.55, maxTtl: 0.55, kind: 'ring' });
    this.burst({ x: p.x, y: p.y, radius: R * 0.7, color: 0xfbbf24, ttl: 0.75, maxTtl: 0.75, kind: 'aoe' });
    for (const e of this.units) {
      if (!e.alive || e.team !== 'enemy') continue;
      if (Math.hypot(e.x - p.x, e.y - p.y) > R + e.radius) continue;
      this.applyDamage(e, p.damage, from ?? e, false, false);
      this.ignite(e);
    }
  }

  /** Đốt 1 đơn vị: DOT 4s + giảm giáp 6s. Dùng chung cho vụ nổ và vùng cháy. */
  private ignite(e: Unit): void {
    e.buffs.push({ id: 'burn', stat: 'hp', mult: 0.9, until: this.time + 4 });
    e.buffs.push({ id: 'sunder', stat: 'def', mult: 0.6, until: this.time + 6 });
    this.pushFloat(e.x, e.y - 30, 'CHÁY!', 0xf97316, 'damage', 1);
  }

  /** Vùng cháy đang hiệu lực — lớp hiệu ứng đọc để vẽ lửa. */
  get fireZonesActive(): FireZone[] {
    return this.fireZones.filter((z) => z.until > this.time);
  }

  /**
   * BẮN THUÊ — máy bay bay qua bay lại thả bom dọc đường bay.
   *
   * Người dùng chốt: *"Gọi máy bay qua bay lại thả bom xuống khu các nơi bay qua"*.
   * Máy bay KHÔNG phải unit (không bị đánh, không tính vào bất kỳ trần nào) — chỉ là hiệu ứng +
   * điểm thả bom chạy ngang sân.
   */
  private tickAirstrike(u: Unit, dt: number): void {
    const st = this.ultState.get(u.id);
    if (!st) return;
    const SPEED = 420;
    st.planeX += SPEED * dt * st.planeDir;
    if (st.planeX > ARENA.w - 40) {
      st.planeX = ARENA.w - 40;
      st.planeDir = -1;
    } else if (st.planeX < 40) {
      st.planeX = 40;
      st.planeDir = 1;
    }
    if (this.time < st.airAt) return;
    st.airAt = this.time + World.AIRSTRIKE_INTERVAL;
    // thả bom xuống khu vực có quái gần đường bay nhất, nếu không có thì thả đại
    const lane = this.units.filter((e) => e.alive && e.team === 'enemy' && Math.abs(e.x - st.planeX) < 120);
    const tx = lane.length ? lane[0].x : st.planeX;
    const ty = lane.length ? lane.reduce((a, e) => a + e.y, 0) / lane.length : ARENA.coreY + 120;
    const R = 70;
    const dmg = u.base.atk * 1.5;
    for (const e of this.units) {
      if (!e.alive || e.team !== 'enemy') continue;
      if (Math.hypot(e.x - tx, e.y - ty) > R + e.radius) continue;
      this.applyDamage(e, dmg, u, false, false);
    }
    this.burst({ x: tx, y: ty, radius: R, color: 0x60a5fa, ttl: 0.55, maxTtl: 0.55, kind: 'aoe' });
    /*
     * MÁY BAY + QUẢ BOM dùng burst kind riêng để lớp hiệu ứng lấy ĐÚNG ảnh Gemini đã sinh
     * (`maybay-parts.png`), thay vì vệt sáng vẽ bằng code. Xem `render/fx.ts` → `texFor`.
     */
    this.burst({ x: st.planeX, y: ARENA.coreY - 150, radius: 52, color: 0xbfdbfe, ttl: 0.5, maxTtl: 0.5, kind: 'plane' });
    this.burst({ x: tx, y: ty - 60, radius: 26, color: 0xfde68a, ttl: 0.45, maxTtl: 0.45, kind: 'firebomb' });
  }

  /** Xoá bản sao khi Phân Thân hết hiệu lực. */
  private despawnClones(owner: Unit): void {
    for (const u of this.units) {
      if (!u.isClone || !u.alive) continue;
      if (u.kind !== owner.kind) continue;
      u.hp = 0;
      u.alive = false;
      u.state = 'dead';
      u.deadAt = this.time;
      this.burst({ x: u.x, y: u.y, radius: 40, color: 0xc4b5fd, ttl: 0.45, maxTtl: 0.45, kind: 'die' });
    }
  }

  /**
   * CHỈ SỐ HIỆU DỤNG của một unit — dùng cho TEST và công cụ cân bằng.
   * Vì sao cần: `effectiveStats()` là hàm rời ở `sim/formulas.ts`; test ultimate cần đo "ATK tăng
   * mạnh" / "tốc đánh giảm mạnh" bằng SỐ THẬT sau khi buff, chứ không đọc lại hằng số.
   */
  statsForTest(u: Unit): Stats {
    return effectiveStats(u);
  }

  /** Ultimate của unit này còn hiệu lực không. */
  ultActive(u: Unit): boolean {
    return u.ultUntil > this.time;
  }

  /** Còn bao lâu mới bấm được ultimate (0 = dùng được ngay). */
  ultCooldownLeft(u: Unit): number {
    return Math.max(0, u.ultReadyAt - this.time);
  }

  /**
   * NGƯỜI DÙNG BẤM NÚT ULTIMATE.
   * Trả `false` nếu: không phải tướng người chơi, đang trong hiệu lực, hoặc chưa hồi xong.
   */
  castUltimate(hero: Unit): boolean {
    if (hero.team !== 'hero' || !hero.alive) return false;
    if (this.ultActive(hero)) return false;
    if (this.ultCooldownLeft(hero) > 0) return false;
    const classId = hero.kind;
    const dur = World.ULT_DURATION[classId] ?? 15;

    hero.ultUntil = dur === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : this.time + dur;
    hero.ultReadyAt = this.time + World.ULT_COOLDOWN;
    this.ultState.set(hero.id, { devourAt: this.time, bombAt: this.time, airAt: this.time, planeX: 0, planeDir: 1, bombZone: -1 });

    switch (classId) {
      case 'strength':
        this.startGiantBoar(hero);
        break;
      case 'agility':
        this.startClones(hero);
        break;
      case 'street':
        /*
         * TẦM ĐÁNH HẾT SÀN ĐẤU — người dùng: *"tầm đánh khi dùng ulti hết sàn đấu"*.
         * Đặt `range` = đường chéo sân (√(1000² + 600²) ≈ 1166) ⇒ không còn khái niệm "ngoài tầm":
         * Street đứng đâu cũng ném tới được mọi vùng có quái, đúng ý "ném liên tục vào các vùng".
         */
        hero.buffs.push({ id: 'ult-bom-range', stat: 'range', mult: 40, until: hero.ultUntil });
        this.pushFloat(hero.x, hero.y - 44, 'BOM XĂNG!', 0xf97316, 'crit', 1.8);
        break;
      case 'army':
        this.startAirstrike(hero);
        break;
      case 'mother':
        this.startConvert(hero);
        break;
      default:
        break;
    }
    this.burst({
      x: hero.x,
      y: hero.y,
      radius: dur === Number.POSITIVE_INFINITY ? 70 : Math.min(110, 50 + dur * 3),
      color: 0xfbbf24,
      ttl: 0.7,
      maxTtl: 0.7,
      kind: 'aura',
    });
    return true;
  }

  /** Dã trư khổng lồ: tăng mạnh ATK/HP/DEF, GIẢM MẠNH tốc đánh + TẦM ĐÁNH = bán kính gầm. */
  private startGiantBoar(hero: Unit): void {
    /*
     * Người dùng chốt: *"Biến thành Dã trư khổng lồ, tăng mạnh sức tấn công, hp và def. Tuy nhiên
     * tốc độ đánh cũng giảm mạnh"*.
     * Dùng `buffs[]` (cơ chế sẵn có, `effectiveStat` nhân dồn) với hạn = `ultUntil`:
     *   atk ×2.4 · hp ×2.2 · def ×2.2 · atkSpeed ×0.45 (giảm mạnh).
     *
     * ── TẦM ĐÁNH KHI HOÁ (task-5) ───────────────────────────────────────────────
     * Người dùng: *"Tầm đánh của strength khi khoá khổng lồ bị vô lý"*.
     * ĐO ĐƯỢC TRÊN GAME THẬT (Playwright, `world.statsForTest`): strength `range` = 66 TRƯỚC và
     * vẫn 66 SAU khi hoá, trong khi `boarRoar()` quét bán kính 120 (đo cut-off: quái radius 17
     * dính ở d = 136, trượt ở d = 140 ⇒ đúng bằng `120 + 17`). Vì đòn đánh chỉ nổ khi
     * `dist <= range + target.radius` (xem `heroAttack`), người chơi phải BÒ SÁT tới 66 mới gầm
     * được, dù tiếng gầm với tới 120 — đúng cái "vô lý" người dùng nói.
     *
     * SỬA: đặt `range` = ĐÚNG bán kính gầm (`World.BOAR_ROAR_RADIUS`) ⇒ "tầm đánh" khớp vùng sát
     * thương thật: đứng ở đâu gầm tới đó thì đánh được tới đó.
     *
     * ⚠️ VÌ SAO TÍNH `mult` THAY VÌ HẰNG SỐ NHƯ `mult: 1.82`:
     * `range` KHÔNG cố định 66 — `classStatsFor()` nhân nó theo sức mạnh class (`classPower`).
     * ĐO ĐƯỢC: power 0 ⇒ 66 · power 5 ⇒ 67,65 · power 20 ⇒ 72,6 · power 50 ⇒ 82,5.
     * ⇒ mult cần để ra 120 là 1,818 ở power 0 nhưng chỉ 1,455 ở power 50. Một hằng số cứng sẽ
     * hoặc non tầm (power thấp) hoặc vượt tầm (power cao). Chia cho `range` hiện tại thì kết quả
     * LUÔN = 120 ở mọi trạng thái meta.
     */
    const until = hero.ultUntil;
    hero.buffs.push({ id: 'ult-boar-atk', stat: 'atk', mult: 2.4, until });
    hero.buffs.push({ id: 'ult-boar-hp', stat: 'hp', mult: 2.2, until });
    hero.buffs.push({ id: 'ult-boar-def', stat: 'def', mult: 2.2, until });
    hero.buffs.push({ id: 'ult-boar-spd', stat: 'atkSpeed', mult: 0.45, until });
    /*
     * Tầm đánh = bán kính gầm. Tính trên `effectiveStat` để nếu có buff range khác đang chạy thì
     * vẫn ra đúng 120 (không cộng dồn thành số khổng lồ). `Math.max(1, ...)` chống chia 0.
     */
    const rangeNow = effectiveStat(hero, 'range');
    hero.buffs.push({
      id: 'ult-boar-range',
      stat: 'range',
      mult: World.BOAR_ROAR_RADIUS / Math.max(1, rangeNow),
      until,
    });
    // hồi thêm đúng phần HP vừa được cộng, nếu không thì "tăng hp" chỉ là trần rỗng
    hero.hp += hero.base.hp * 1.2;
    this.pushFloat(hero.x, hero.y - 48, 'DÃ TRƯ!', 0xfbbf24, 'crit', 2);
  }

  /**
   * CHOÁNG 1 đơn vị — BOSS MIỄN NHIỄM.
   * Người dùng chốt: *"gây choáng… Không thể choáng boss"*.
   */
  applyStun(target: Unit, seconds: number): boolean {
    if (!target.alive || target.isBoss) return false;
    target.stunUntil = Math.max(target.stunUntil, this.time + seconds);
    target.state = 'idle';
    target.targetId = null;
    this.pushFloat(target.x, target.y - 30, 'CHOÁNG!', 0xfbbf24, 'crit', 1.2);
    return true;
  }

  /** Đơn vị có đang bị choáng không. */
  isStunned(u: Unit): boolean {
    return u.stunUntil > this.time;
  }

  /**
   * TIẾNG GẦM AOE của Dã trư — thay cho đòn đánh thường.
   * Người dùng: *"đánh thường biến thành tiếng gầm AOE dame và gây choáng"*.
   *
   * ⚠️ BÁN KÍNH LẤY TỪ `World.BOAR_ROAR_RADIUS`, KHÔNG viết lại số 120 ở đây: `startGiantBoar()`
   * đặt tầm đánh = chính hằng số đó. Hai chỗ lệch nhau là quay lại đúng lỗi "tầm đánh vô lý"
   * (task-5) — nên chỉ còn MỘT nguồn số.
   */
  boarRoar(hero: Unit, targets: Unit[]): void {
    const R = World.BOAR_ROAR_RADIUS;
    const dmg = hero.base.atk * 1.8;
    for (const e of targets) {
      if (!e.alive || e.team !== 'enemy') continue;
      if (Math.hypot(e.x - hero.x, e.y - hero.y) > R + e.radius) continue;
      this.applyDamage(e, dmg, hero, false, false);
      this.applyStun(e, 1.2); // boss bị từ chối bên trong `applyStun`
    }
    this.burst({ x: hero.x, y: hero.y, radius: R, color: 0xfbbf24, ttl: 0.55, maxTtl: 0.55, kind: 'ring' });
  }

  /**
   * PHÂN THÂN (Agility) — tạo 3 bản sao y hệt, rải trên bản đồ.
   *
   * ⚠️ Người dùng chốt **(B)**: *"phân thân có thể di chuyển và người dùng có thể điều khiển và
   * cũng dùng skill như nhân vật được nhưng skill của phân thân luôn luôn tự động dùng, người dùng
   * không manual dùng skill được"* — nghĩa là bản sao TỰ ĐÁNH/ TỰ NIỆM CHIÊU, người chơi chỉ điều
   * khiển con GỐC. Vì vậy bản sao được đánh dấu `isClone` và **không** nhận lệnh click.
   */
  private startClones(hero: Unit): void {
    const n = 3;
    for (let i = 0; i < n; i++) {
      // rải quanh Nhà theo 3 hướng khác nhau, không chồng lên nhau
      const ang = -Math.PI / 2 + ((i + 1) / (n + 1)) * Math.PI * 1.4;
      const d = 150 + i * 22;
      const cx = Math.max(40, Math.min(ARENA.w - 40, this.core.x + Math.cos(ang) * d));
      const cy = Math.max(ARENA.coreY + 60, Math.min(ARENA.h - 40, this.core.y + Math.sin(ang) * d));
      const clone = this.makeUnit(hero.kind, 'hero', cx, cy, { ...hero.base }, hero.radius);
      clone.hp = hero.hp;
      clone.homeX = cx;
      clone.homeY = cy;
      clone.isClone = true;
      this.units.push(clone);
      clone.ultUntil = hero.ultUntil;
      clone.mana = hero.mana;
      clone.skillCds = hero.skillCds.slice();
      // bản sao tự niệm chiêu
      clone.autoCastCd = 0;
      this.pushFloat(cx, cy - 40, 'PHÂN THÂN', 0xc4b5fd, 'dodge', 1.4);
    }
  }

  /** Máy bay: khởi tạo đường bay (trái→phải) cho lượt đầu. */
  private startAirstrike(hero: Unit): void {
    const st = this.ultState.get(hero.id);
    if (!st) return;
    st.planeX = 60;
    st.planeDir = 1;
    this.pushFloat(hero.x, hero.y - 48, 'MÁY BAY!', 0x60a5fa, 'crit', 2);
  }

  /**
   * THU PHỤC (Đẻ mướn) — 20% số quái ĐANG CÓ TRÊN MÀN đổi sang quân mình.
   *
   * Người dùng chốt: *"1/5 quái đang xuất hiện trên sân (random chứ enemy chứ không ưu tiên enemy
   * ở gần) thành quân của mình (trừ boss), không có đếm lùi thời gian chỉ khi enemy được thu phục
   * bị chết… 20% của số quái đang xuất hiện trên màn hình"*.
   *   · RANDOM, không ưu tiên gần.
   *   · KHÔNG boss.
   *   · Đánh dấu `converted` để: (a) không tính vào 15 con đẻ, (b) TRẢ VỀ làm quái khi hết.
   */
  private startConvert(hero: Unit): void {
    const cands = this.units.filter((u) => u.alive && u.team === 'enemy' && !u.isBoss);
    const want = Math.max(1, Math.floor(cands.length / 5));
    // xáo trộn bằng RNG của sim (tất định theo seed) rồi lấy `want` con đầu
    const pool = cands.slice();
    for (let i = pool.length - 1; i > 0; i--) {
      const j = this.rng.int(0, i);
      const t = pool[i];
      pool[i] = pool[j];
      pool[j] = t;
    }
    for (const e of pool.slice(0, want)) {
      this.convertToAlly(e, hero);
    }
    if (want > 0) this.pushFloat(hero.x, hero.y - 48, `THU PHỤC ${pool.slice(0, want).length}`, 0xf472b6, 'crit', 2);
  }

  /** Đổi 1 quái thành quân mình (dùng chung cho ultimate Thu Phục). */
  private convertToAlly(e: Unit, owner: Unit): void {
    /*
     * ⚠️ CHỤP TRẠNG THÁI TRƯỚC KHI ĐỔI PHE — bắt buộc, vì luật đòi trả lại ĐÚNG máu và hình ảnh
     * *"lúc bắt đầu bị thu phục"*. Chụp SAU khi đổi phe thì đã mất dữ liệu gốc.
     */
    e.convertedSnapshot = { hp: e.hp, kind: e.kind, baseHp: e.base.hp, x: e.x, y: e.y };
    e.convertedFrom = e.kind;
    e.isConverted = true;
    e.team = 'hero';
    // quân thu phục KHÔNG phải tướng ⇒ tách khỏi mọi bảng đếm tướng
    e.minions = 0;
    e.targetId = null;
    e.state = 'idle';
    e.orderX = null;
    e.orderY = null;
    // giữ chỉ số quái nhưng đánh cho phe ta
    this.convertedOwners.set(e.id, owner.id);
    this.pushFloat(e.x, e.y - 34, 'THEO TA!', 0xf472b6, 'heal', 1.4);
  }

  /**
   * TRẢ QUÂN THU PHỤC VỀ LÀM QUÁI.
   *
   * Người dùng chốt: *"Khi enemy hết hiệu lực hoặc không còn quái trên sân (khi kết thúc màn chơi),
   * sẽ trở lại làm quái"*.
   */
  revertConverted(): number {
    let n = 0;
    for (const u of this.units) {
      if (!u.isConverted || !u.alive) continue;
      const snap = u.convertedSnapshot;
      u.isConverted = false;
      u.team = 'enemy';
      /*
       * TRẢ LẠI MÁU VÀ HÌNH ẢNH NHƯ LÚC BỊ THU PHỤC.
       *
       * Người dùng: *"phải trả lại số máu và hình ảnh của enemy đó lúc bắt đầu bị thu phục"*.
       *   · Máu: về ĐÚNG `snap.hp` — không giữ máu hiện tại (nó có thể đã mất máu khi đánh cho phe
       *     ta, hoặc được hồi). Đây là điều người dùng yêu cầu rõ nhất.
       *   · Hình: `kind` khôi phục về loại quái gốc. `render/unitview.ts` chọn rig theo nhánh
       *     `team === 'hero' ? class : quái` ⇒ đổi `team` về 'enemy' là hình TỰ trở lại hình quái;
       *     khôi phục `kind` để chắc chắn đúng LOẠI quái ban đầu.
       *   · `base.hp` cũng trả về như cũ, nếu không thanh máu sẽ vẽ sai tỉ lệ (hp > base).
       */
      if (snap) {
        u.kind = snap.kind;
        u.base.hp = snap.baseHp;
        u.hp = Math.max(1, Math.min(snap.hp, snap.baseHp));
        // về lại chỗ cũ; nếu toạ độ cũ nằm ngoài sân (hiếm) thì để nguyên vị trí hiện tại
        if (Number.isFinite(snap.x) && Number.isFinite(snap.y)) {
          u.x = snap.x;
          u.y = snap.y;
          u.homeX = snap.x;
          u.homeY = snap.y;
        }
      } else {
        // save/ trạng thái cũ không có snapshot ⇒ chỉ bảo đảm máu không âm
        u.hp = Math.max(1, Math.min(u.hp, u.base.hp));
      }
      u.convertedSnapshot = undefined;
      u.convertedFrom = undefined;
      u.targetId = null;
      u.state = 'idle';
      u.minions = 0;
      this.convertedOwners.delete(u.id);
      n++;
    }
    return n;
  }

  /** Người chơi bấm tay. Trả về true nếu cast được. */
  castSkill(hero: Unit, index: number, fromAuto = false): boolean {
    const cls = CLASSES[hero.kind as ClassId];
    // ⚠️ Niệm theo BỘ ĐANG TRANG BỊ (7 ô), không phải 7 chiêu gốc trong data
    const sk = this.skillsFor(hero.kind)[index];
    if (!sk || !hero.alive) return false;
    if (this.wave < sk.unlockWave) {
      if (!fromAuto) this.events.push({ type: 'error', text: `Chiêu mở ở wave ${sk.unlockWave}` });
      return false;
    }
    if (hero.mana < sk.manaCost) {
      if (!fromAuto) this.events.push({ type: 'error', text: 'Không đủ mana' });
      return false;
    }
    if ((hero.skillCds[index] ?? 0) > this.time) {
      if (!fromAuto) this.events.push({ type: 'error', text: 'Chiêu đang hồi' });
      return false;
    }
    const s = effectiveStats(hero);
    hero.mana -= sk.manaCost;
    hero.skillCds[index] = this.time + sk.cooldown;
    // ---- DÁNG RA CHIÊU: khớp với tên chiêu (xem data/skill-anims.ts)
    const anim = animOf(sk.anim ?? sk.kind);
    hero.castAnim = sk.anim ?? sk.kind;
    hero.castT0 = this.time;
    hero.castDur = anim.dur;
    // KHÔNG xoá `swing`: dáng ra chiêu tự được ưu tiên trong applyPose, còn cú đánh
    // thường đang dở thì tiếp tục ngay sau khi chiêu diễn xong (tránh "đứng hình").
    // hiệu ứng + rung màn hình theo độ "nặng" của chiêu
    const w = anim.weight;
    this.burst({
      x: hero.x, y: hero.y - 14, radius: 24 + w * 9,
      color: anim.color, ttl: 0.4 + w * 0.05, maxTtl: 0.4 + w * 0.05,
      kind: anim.fx === 'none' ? 'skill' : (anim.fx as EffectBurst['kind']),
    });
    /*
     * "BÙNG NỔ RA CHIÊU" — 3 lớp: loé sáng tại người → vòng xung kích → bụi dưới chân.
     * Chiêu càng nặng (weight) thì 3 lớp càng to; tuyệt kỹ còn khựng hình + rung màn hình.
     */
    this.burst({ x: hero.x, y: hero.y - 16, radius: 26 + w * 12, color: 0xffffff, ttl: 0.2, maxTtl: 0.2, kind: 'flash', scale: 0.5 + w * 0.1 });
    this.burst({ x: hero.x, y: hero.y - 10, radius: 34 + w * 14, color: anim.color, ttl: 0.45 + w * 0.04, maxTtl: 0.45 + w * 0.04, kind: 'ring', scale: 0.8 + w * 0.08 });
    this.burst({ x: hero.x, y: hero.y + 2, radius: 30 + w * 10, color: 0xcbb79a, ttl: 0.5, maxTtl: 0.5, kind: 'dust' });
    if (w >= 4) {
      this.hitStop = Math.max(this.hitStop, 0.06 + w * 0.012);
      this.shockPower = Math.max(this.shockPower, 0.3 + w * 0.12);
      // tia sáng bắn ra 4 phía cho chiêu "nặng"
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        this.burst({ x: hero.x, y: hero.y - 14, radius: 12 + w * 3, color: anim.color, ttl: 0.4, maxTtl: 0.4, kind: 'spark', angle: a, len: 60 + w * 22 });
      }
    }
    if (anim.weight >= 3) this.events.push({ type: 'cast', x: hero.x, y: hero.y, text: sk.name, power: anim.weight });
    this.events.push({ type: 'cast', x: hero.x, y: hero.y, text: sk.name, power: 1 });
    this.pushFloat(hero.x, hero.y - 52, sk.name, 0xa78bfa, 'crit', 1.4);

    switch (sk.kind) {
      case 'aoeDamage':
        this.aoeDamage(hero, s.atk * (sk.mult ?? 1), sk.radius ?? 120, sk.maxTargets ?? 8);
        break;
      case 'projectile': {
        const targets = this.enemiesNear(hero.x, hero.y, 320);
        const count = sk.count ?? 3;
        for (let i = 0; i < count; i++) {
          const t = targets[i % Math.max(1, targets.length)] ?? this.pickNearestEnemy(hero, 400);
          if (!t) break;
          const spread = (sk.spread ?? 0.5) * (i / Math.max(1, count) - 0.5);
          this.spawnProjectileSpread(hero, t, s.atk * (sk.mult ?? 1), spread, sk.projectileSpeed ?? 600, sk.id);
        }
        break;
      }
      case 'heal':
        if (cls.usesAmmo) {
          hero.ammo += sk.amount ?? 30;
          this.pushFloat(hero.x, hero.y - 40, `+${sk.amount ?? 30} đạn`, 0xfde68a, 'heal');
        } else {
          hero.hp = Math.min(s.hp, hero.hp + (sk.amount ?? 200));
          this.pushFloat(hero.x, hero.y - 40, `+${sk.amount ?? 200}`, 0x4ade80, 'heal');
        }
        break;
      case 'buff':
        addBuff(hero, { id: sk.id, stat: sk.stat ?? 'atk', mult: sk.buffMult ?? 1.3, duration: sk.duration ?? 5 }, this.time);
        this.burst({ x: hero.x, y: hero.y, radius: 70, color: 0x38bdf8, ttl: 0.5, maxTtl: 0.5, kind: 'skill' });
        break;
      case 'debuff':
        this.debuffArea(hero, sk);
        break;
      case 'summon':
        this.summonMinions(hero, sk.count ?? 2, sk.summonKind ?? 'brat', sk.duration ?? 40);
        break;
      case 'invuln':
        hero.invulnUntil = this.time + (sk.duration ?? 3);
        this.burst({ x: hero.x, y: hero.y, radius: 90, color: 0xfde68a, ttl: 0.6, maxTtl: 0.6, kind: 'skill' });
        break;
      case 'shield':
        hero.shield = s.hp * (sk.pct ?? 0.3);
        hero.shieldUntil = this.time + (sk.duration ?? 6);
        break;
      case 'dashStrike': {
        const t = this.pickNearestEnemy(hero, 400);
        if (t) {
          const dx = t.x - hero.x;
          const dy = t.y - hero.y;
          const len = Math.hypot(dx, dy) || 1;
          const d = Math.min(sk.distance ?? 200, len - t.radius);
          const a = Math.atan2(dy, dx);
          // VỆT TỐC ĐỘ dọc đường lướt: 4 vệt mờ dần ⇒ đọc ra "Ảnh Bộ" chứ không phải dịch chuyển tức thời
          for (let i = 1; i <= 4; i++) {
            const f = i / 5;
            this.burst({
              x: hero.x + Math.cos(a) * d * f, y: hero.y + Math.sin(a) * d * f - 12,
              radius: 10, color: 0x67e8f9, ttl: 0.24 + f * 0.12, maxTtl: 0.36, kind: 'spark', angle: a + Math.PI, len: 46,
            });
          }
          this.burst({ x: hero.x, y: hero.y - 12, radius: 34, color: 0x67e8f9, ttl: 0.3, maxTtl: 0.3, kind: 'streak', angle: a, len: d * 0.7 });
          hero.x += (dx / len) * d;
          hero.y += (dy / len) * d;
          this.burst({ x: hero.x, y: hero.y - 12, radius: 40, color: 0x67e8f9, ttl: 0.3, maxTtl: 0.3, kind: 'ring', scale: 0.9 });
          const ts = effectiveStats(t);
          const res = computeDamage({ atk: s.atk, multiplier: sk.mult ?? 2, def: ts.def, targetMaxHp: t.base.hp, isEliteOrBoss: t.isElite || t.isBoss });
          this.applyDamage(t, res.damage, hero, false, res.floored);
        }
        break;
      }
    }
    return true;
  }

  private spawnProjectileSpread(from: Unit, target: Unit, atk: number, spread: number, speed: number, skin?: string): void {
    const dx = target.x - from.x;
    const dy = target.y - from.y;
    const base = Math.atan2(dy, dx) + spread;
    this.projectiles.push({
      id: nextId++,
      team: from.team,
      x: from.x,
      y: from.y - 14,
      vx: Math.cos(base) * speed,
      vy: Math.sin(base) * speed,
      damage: atk,
      ownerId: from.id,
      ttl: 2,
      radius: 7,
      color: 0xa78bfa,
      kind: 'thrown',
      skin,
    });
  }

  private aoeDamage(from: Unit, atk: number, radius: number, maxTargets: number): void {
    const targets = this.enemiesNear(from.x, from.y, radius).slice(0, maxTargets);
    // NỔ DIỆN RỘNG: vòng xung kích + nứt đất + bụi + tia toả tròn (xem blastBursts)
    this.blastBursts(from.x, from.y, radius, 0xa78bfa, Math.min(1, radius / 300));
    for (const t of targets) {
      const ts = effectiveStats(t);
      const res = computeDamage({ atk, def: ts.def, targetMaxHp: t.base.hp, isEliteOrBoss: t.isElite || t.isBoss });
      this.applyDamage(t, res.damage, from, false, res.floored);
    }
  }

  private debuffArea(from: Unit, sk: { stat?: keyof Stats; buffMult?: number; duration?: number; radius?: number; maxTargets?: number; id: string }): void {
    const targets = this.enemiesNear(from.x, from.y, sk.radius ?? 200).slice(0, sk.maxTargets ?? 99);
    for (const t of targets) {
      const buff: Buff = {
        id: sk.id,
        stat: sk.stat ?? 'atk',
        mult: sk.buffMult ?? 0.7,
        until: this.time + (sk.duration ?? 5),
      };
      t.buffs.push(buff);
      this.pushFloat(t.x, t.y - 40, sk.id === 'taunt' ? 'TỨC!' : 'NHỤC!', 0xfacc15, 'crit', 1.0);
    }
    // SÓNG "NHỤC MẠ" — sóng lan rộng màu vàng, không gây sát thương nên chỉ 1 lớp vòng + bụi chữ
    const r = sk.radius ?? 200;
    this.burst({ x: from.x, y: from.y, radius: r, color: 0xfacc15, ttl: 0.5, maxTtl: 0.5, kind: 'ring', scale: 1.05 });
    this.burst({ x: from.x, y: from.y, radius: r * 0.7, color: 0xfde68a, ttl: 0.42, maxTtl: 0.42, kind: 'swirl', spin: 2.4 });
    const ringN = 10;
    for (let i = 0; i < ringN; i++) {
      const a = (i / ringN) * Math.PI * 2;
      this.burst({ x: from.x + Math.cos(a) * r * 0.5, y: from.y + Math.sin(a) * r * 0.45, radius: 10, color: 0xfacc15, ttl: 0.4, maxTtl: 0.4, kind: 'dust', scale: 0.7 });
    }
  }

  private summonMinions(owner: Unit, count: number, kind: string, duration = 40): void {
    // TRẦN TỔNG 15 CON (người dùng chốt), không phải "20 con mỗi tướng"
    const room = Math.max(0, MAX_MINIONS_TOTAL - this.minionCount());
    const n = Math.min(count, room);
    for (let i = 0; i < n; i++) {
      const def = ENEMIES[kind] ?? ENEMIES.brat;
      const a = this.rng.range(0, Math.PI * 2);
      const r = this.rng.range(30, 66);
      const u = this.makeUnit(kind, 'hero', owner.x + Math.cos(a) * r, owner.y + Math.sin(a) * r, def.base, def.radius);
      u.base = { ...def.base };
      u.hp = u.base.hp;
      u.minions = 1;
      // Ô TỤ QUÂN: mỗi con một ô trên vòng tròn quanh Nhà ⇒ không dồn cục rồi kẹt
      u.minionSlot = this.nextMinionSlot();
      this.units.push(u);
      this.burst({ x: u.x, y: u.y, radius: 20, color: 0xf472b6, ttl: 0.35, maxTtl: 0.35, kind: 'spawn' });
    }
    if (n > 0) this.pushFloat(owner.x, owner.y - 56, `+${n} con`, 0xf472b6, 'heal', 1.1);
    void duration;
  }

  /** Cấp ô tụ quân còn trống (0..MAX_MINIONS_TOTAL-1). */
  private nextMinionSlot(): number {
    const used = new Set<number>();
    for (const u of this.units) if (u.alive && u.minions > 0 && u.minionSlot !== undefined) used.add(u.minionSlot);
    for (let i = 0; i < MAX_MINIONS_TOTAL; i++) if (!used.has(i)) return i;
    return this.minionCount() % MAX_MINIONS_TOTAL;
  }

  /** Vị trí TỤ QUÂN của 1 đệ tử — vòng tròn quanh Nhà, chia đều theo ô. */
  private rallySpot(u: Unit): { x: number; y: number } {
    const slot = u.minionSlot ?? 0;
    // chia 15 ô thành 5 vành × 3 góc ⇒ hai con cạnh nhau cách ~42px, không chen nhau
    const a = (slot % 5) * ((Math.PI * 2) / 5) + Math.floor(slot / 5) * 0.42 + 0.4;
    const r = this.core.radius + 34 + Math.floor(slot / 5) * 22;
    return { x: this.core.x + Math.cos(a) * r, y: this.core.y + Math.sin(a) * r };
  }

  private spawnMinionAt(owner: Unit, kind: string): void {
    const def = ENEMIES[kind] ?? ENEMIES.thug;
    const enemyCount = this.enemies.length;
    if (enemyCount > 90) return; // CAP số quân — chống vỡ hiệu năng
    const a = this.rng.range(0, Math.PI * 2);
    const u = this.makeUnit(kind, 'enemy', owner.x + Math.cos(a) * 34, owner.y + Math.sin(a) * 34, def.base, def.radius);
    u.scale = 1;
    this.units.push(u);
    this.burst({ x: u.x, y: u.y, radius: 20, color: 0x9333ea, ttl: 0.3, maxTtl: 0.3, kind: 'spawn' });
  }

  // ------------------------------------------------------------ bình thuốc

  usePotion(kind: PotionKind, heroId: number): { ok: boolean; message: string } {
    if (this.potions[kind] <= 0) return { ok: false, message: `Hết ${POTIONS[kind].name}` };
    const hero = this.units.find((u) => u.id === heroId && u.team === 'hero' && u.alive);
    if (!hero) return { ok: false, message: 'Chưa chọn tướng' };
    if (kind === 'stamina' && this.staminaPotionsUsed >= MAX_STAMINA_POTIONS_PER_STAGE) {
      return { ok: false, message: 'Đã dùng tối đa bình thể lực trong màn này' };
    }

    const s = effectiveStats(hero);
    const def = POTIONS[kind];
    if (kind === 'hp') {
      hero.hp = Math.min(s.hp, hero.hp + s.hp * (def.pct ?? 0.4));
      this.pushFloat(hero.x, hero.y - 46, `+${Math.round(s.hp * (def.pct ?? 0.4))}`, 0x4ade80, 'heal');
    } else if (kind === 'mana') {
      hero.mana = Math.min(s.mana, hero.mana + s.mana * (def.pct ?? 0.5));
      this.pushFloat(hero.x, hero.y - 46, 'MANA!', 0x60a5fa, 'heal');
    } else {
      hero.invulnUntil = this.time + (def.duration ?? 5);
      addBuff(hero, { id: 'staminaPotion', stat: 'allPower', mult: def.powerMult ?? 1.5, duration: def.duration ?? 5 }, this.time);
      this.staminaPotionsUsed += 1;
      this.pushFloat(hero.x, hero.y - 56, 'BẤT TỬ!', 0xfbbf24, 'crit', 1.6);
      this.burst({ x: hero.x, y: hero.y, radius: 110, color: 0xfbbf24, ttl: 0.7, maxTtl: 0.7, kind: 'skill' });
    }
    this.potions[kind] -= 1;
    this.events.push({ type: 'potion', x: hero.x, y: hero.y, text: def.name });
    return { ok: true, message: `Dùng ${def.name}` };
  }

  buyAmmo(heroId: number): { ok: boolean; message: string } {
    const hero = this.units.find((u) => u.id === heroId && u.team === 'hero' && u.alive);
    if (!hero) return { ok: false, message: 'Chưa chọn tướng' };
    if (CLASSES[hero.kind as ClassId]?.usesAmmo !== true) return { ok: false, message: 'Tướng này không dùng đạn' };
    if (this.gold < AMMO_PACK_PRICE) return { ok: false, message: `Cần ${AMMO_PACK_PRICE} vàng` };
    this.gold -= AMMO_PACK_PRICE;
    hero.ammo += AMMO_PACK_SIZE;
    this.pushFloat(hero.x, hero.y - 46, `+${AMMO_PACK_SIZE} đạn`, 0xfde68a, 'heal');
    return { ok: true, message: `Mua ${AMMO_PACK_SIZE} viên đạn` };
  }

  // ------------------------------------------------------------ vật lý & tiện ích

  private updateProjectiles(dt: number): void {
    for (const p of this.projectiles) {
      p.ttl -= dt;
      /*
       * CHAI RƯỢU CỦA ULTIMATE STREET — ĐI THEO CUNG RỒI NỔ Ở CUỐI, KHÔNG va chạm giữa đường.
       *
       * Người dùng: *"ném chai rượu bay theo đường vòng cung vào 1 khu vực và gây dame aoe trong khu
       * vực đó"* ⇒ nếu để va chạm như đạn thường thì chai nổ ngay khi chạm quái ĐẦU TIÊN, không bao
       * giờ tới được "khu vực" đã nhắm. Nay bỏ qua va chạm, đếm tiến trình cung, tới nơi mới nổ.
       */
      if (p.explodeAt !== undefined) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        // `arcT`: 0 lúc rời tay → 1 lúc chạm đất. Dùng `flightTotal` ĐÃ LƯU (xem chú thích ở types).
        const total = Math.max(0.0001, p.flightTotal ?? 1);
        p.arcT = Math.min(1, Math.max(0, 1 - Math.max(0, p.ttl) / total));
        if (p.ttl <= 0 || this.time >= (p.explodeAt ?? Infinity)) {
          this.firebombExplode(p);
          p.ttl = -1;
        }
        continue;
      }
      /*
       * ⚠️ KIỂM VA CHẠM THEO CẢ ĐOẠN ĐƯỜNG ĐI, KHÔNG CHỈ ĐIỂM CUỐI.
       *
       * Đạn đi `v·dt` mỗi khung. Ở 60fps đạn súng (780px/s) đi 13px/khung — với quái đường kính
       * ~44px thì còn kịp chạm. Nhưng khi fps tụt (máy yếu, nhiều quái, vừa vào wave) `dt` tăng
       * ⇒ 30fps là 26px/khung, 20fps là 39px/khung ⇒ **đạn XUYÊN QUA quái mà không nổ**. Người
       * chơi thấy đúng hiện tượng "bắn trúng mà không mất máu".
       *
       * Cách sửa: lấy đoạn từ vị trí CŨ tới vị trí MỚI rồi đo khoảng cách NGẮN NHẤT từ tâm quái
       * tới đoạn đó (chuẩn, không phụ thuộc fps) thay vì chỉ so khoảng cách tới điểm cuối.
       */
      const fromX = p.x;
      const fromY = p.y;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const pool = p.team === 'hero' ? this.enemies : this.heroes;
      for (const t of pool) {
        if (World.distToSegment(t.x, t.y, fromX, fromY, p.x, p.y) <= t.radius + p.radius) {
          const ts = effectiveStats(t);
          const owner = this.unitByIdPublic(p.ownerId);
          const res = computeDamage({
            atk: p.damage,
            def: ts.def,
            targetMaxHp: t.base.hp,
            isEliteOrBoss: p.team === 'enemy' ? (owner?.isBoss ?? false) || (owner?.isElite ?? false) : t.isElite || t.isBoss,
          });
          this.applyDamage(t, res.damage, owner ?? t, false, res.floored);
          p.ttl = -1;
          break;
        }
      }
    }
    this.projectiles = this.projectiles.filter((p) => p.ttl > 0 && p.x > -60 && p.x < ARENA.w + 60 && p.y > -60 && p.y < ARENA.h + 60);
    this.updateFireZones(dt);
  }

  /**
   * VÙNG CHÁY — gây thiêu đốt cho quái ĐANG ĐỨNG TRONG vùng, kể cả con đi vào SAU khi chai nổ.
   *
   * Người dùng: *"có hiệu ứng cháy phạm vi ảnh hưởng, enemy đang bị thiêu đốt cũng sẽ có hiệu ứng
   * cháy trên người"* ⇒ vùng phải là dữ liệu THẬT của sim (không chỉ vẽ ở client), nếu không thì
   * "cháy" chỉ là hình mà quái không mất máu.
   */
  private updateFireZones(dt: number): void {
    void dt;
    for (const z of this.fireZones) {
      if (z.until <= this.time) continue;
      for (const e of this.units) {
        if (!e.alive || e.team !== 'enemy') continue;
        if (Math.hypot(e.x - z.x, e.y - z.y) > z.radius + e.radius) continue;
        // làm mới DOT mỗi nhịp để con nằm trong vùng cháy liên tục
        const burn = e.buffs.find((b) => b.id === 'burn');
        if (burn) burn.until = Math.max(burn.until, this.time + 0.6);
        else this.ignite(e);
      }
    }
    this.fireZones = this.fireZones.filter((z) => z.until > this.time);
  }

  /**
   * KHOẢNG CÁCH NGẮN NHẤT từ điểm (px,py) tới ĐOẠN thẳng (ax,ay)-(bx,by).
   * Dùng cho va chạm đạn: đạn đi một đoạn mỗi khung nên phải kiểm cả đoạn, không chỉ điểm cuối
   * (xem chú thích ở vòng cập nhật đạn).
   */
  private static distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(px - ax, py - ay);
    // chiếu điểm lên đoạn, kẹp về [0,1] ⇒ điểm gần nhất trên đoạn
    let k = ((px - ax) * dx + (py - ay) * dy) / lenSq;
    k = k < 0 ? 0 : k > 1 ? 1 : k;
    return Math.hypot(px - (ax + k * dx), py - (ay + k * dy));
  }

  /** Đẩy tách để quái không chồng lên nhau. */
  /**
   * ĐẨY NHAU RA khi chồng lấn (không có engine vật lý).
   *
   * ⚠️ NGƯỜI DÙNG BÁO: "con của mẹ thiên hạ đánh không được phép có tác dụng đẩy lùi".
   * Game KHÔNG có knockback khi đánh — cái người chơi thấy là ĐỆ TỬ ĐẨY QUÁI qua hàm này
   * (một bầy 15 con vây quanh thì con quái bị đẩy trôi đi như bị knockback).
   * Sửa: ĐỆ TỬ NHƯỜNG HẾT — đụng quái thì chỉ đệ tử dịch, quái đứng yên; còn lại chia
   * theo "khối lượng" (boss nặng hơn nên nhích ít hơn).
   */
  private separateUnits(): void {
    const list = this.units.filter((u) => u.alive);
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      for (let j = i + 1; j < list.length; j++) {
        const b = list[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        /*
         * ⚠️ ĐỆ TỬ ĐƯỢC PHÉP CHỒNG NHAU MỘT PHẦN.
         * Đo được: cho 15 đệ tử tách đủ như người lớn thì chúng đẩy nhau ra khỏi ô tụ quân
         * liên tục ⇒ cứ đi về rồi bị đẩy ra, trung vị dịch chuyển 40px/2s (nhìn như "kẹt"
         * và rung). Đây là bầy trẻ con chen nhau — cho chồng 45% thì chúng đứng yên thật.
         */
        const bothMinions = a.minions > 0 && b.minions > 0;
        const min = (a.radius + b.radius) * (bothMinions ? 0.55 : 1);
        const d2 = dx * dx + dy * dy;
        if (d2 > 0.0001 && d2 < min * min) {
          const d = Math.sqrt(d2);
          const overlap = min - d;
          const nx = dx / d;
          const ny = dy / d;
          /*
           * ĐỆ TỬ NHƯỜNG HẾT: đụng QUÁI thì chỉ đệ tử dịch, quái ĐỨNG YÊN.
           * Đây chính là "con của mẹ thiên hạ đánh không được đẩy lùi quái" — game không
           * có knockback, cái người chơi thấy là đệ tử đẩy quái qua hàm này.
           *
           * ⚠️ BẪY ĐÃ DÍNH: tôi từng cho BOSS "nặng" nên nhích ít hơn (0,15 thay vì 1).
           * Nghe hợp lý nhưng nó đổi HÌNH HỌC của cả wave 1 (mỗi wave đều có boss) ⇒ tướng
           * bị đẩy khác đi, lời hứa "wave 1 tank mất dưới 5% máu" vỡ (đo 40 seed: 93,8%).
           * Không có yêu cầu nào về boss nặng ⇒ bỏ hẳn, chỉ giữ luật cho đệ tử.
           */
          const aYields = a.minions > 0 && b.team === 'enemy' && b.minions === 0;
          const bYields = b.minions > 0 && a.team === 'enemy' && a.minions === 0;
          if (!aYields && !bYields) {
            const push = overlap / 2;
            a.x -= nx * push;
            a.y -= ny * push;
            b.x += nx * push;
            b.y += ny * push;
          } else if (aYields) {
            a.x -= nx * overlap;
            a.y -= ny * overlap;
          } else {
            b.x += nx * overlap;
            b.y += ny * overlap;
          }
        }
      }
    }
    for (const u of this.units) {
      if (!u.alive) continue;
      u.x = Math.max(8, Math.min(ARENA.w - 8, u.x));
      u.y = Math.max(8, Math.min(ARENA.h - 8, u.y));
    }

    // NHÀ LÀ VẬT CẢN: không ai được đứng trên mái nhà.
    // Đúng về hình ảnh và buộc hàng thủ phải dàn ra xung quanh.
    for (const u of this.units) {
      if (!u.alive) continue;
      const dx = u.x - this.core.x;
      const dy = u.y - this.core.y;
      const d = Math.hypot(dx, dy);
      const min = this.core.radius + u.radius;
      if (d >= min) continue;
      if (d < 0.001) {
        u.x = this.core.x + min;
      } else {
        u.x = this.core.x + (dx / d) * min;
        u.y = this.core.y + (dy / d) * min;
      }
    }
  }

  private cleanupDead(): void {
    // LUÔN dọn xác sau 0.8s (đủ để chạy hoạt ảnh ngã).
    // Trước đây có guard `if (this.units.length < 400) return;` — SAI: nó khiến
    // xác không bao giờ được xoá ⇒ "bóng mờ" và số đơn vị phình mãi.
    let hasExpired = false;
    for (const u of this.units) {
      if (!u.alive && this.time - u.deadAt > 0.8) {
        hasExpired = true;
        break;
      }
    }
    if (!hasExpired) return;
    this.units = this.units.filter((u) => u.alive || this.time - u.deadAt <= 0.8);
  }

  private updateVisuals(dt: number): void {
    for (const f of this.floats) {
      f.ttl -= dt;
      f.y -= dt * 42;
    }
    this.floats = this.floats.filter((f) => f.ttl > 0);
    for (const b of this.bursts) {
      b.ttl -= dt;
      b.radius += dt * 60;
    }
    this.bursts = this.bursts.filter((b) => b.ttl > 0);
  }

  private checkWaveEnd(): void {
    const pending = this.spawnQueue.length > 0;
    const alive = this.enemies.length > 0;
    if (pending || alive) return;

    // hết wave
    this.gold += (this.waveDef?.rewardGold ?? 0);
    // HỒI SINH tướng đã gục (50% máu) — nếu không, đội hình sẽ mất dần và game không thể qua
    for (const u of this.units) {
      if (!isClassHero(u)) continue;
      if (!u.alive) {
        u.alive = true;
        u.state = 'idle';
        u.buffs = [];
        u.hp = u.base.hp * 0.5;
        this.heroHome.set(u.id, { x: u.homeX, y: u.homeY });
        this.burst({ x: u.x, y: u.y, radius: 40, color: 0xfbbf24, ttl: 0.5, maxTtl: 0.5, kind: 'spawn' });
      } else {
        const s = effectiveStats(u);
        u.hp = Math.min(s.hp, u.hp + s.hp * 0.35);
      }
      const s2 = effectiveStats(u);
      u.mana = s2.mana;
      u.ammo += CLASSES[u.kind as ClassId]?.usesAmmo ? 25 : 0;
    }
    /*
     * QUÂN THU PHỤC TRỞ LẠI LÀM QUÁI KHI HẾT MÀN.
     * Người dùng chốt: *"Khi enemy hết hiệu lực hoặc không còn quái trên sân (khi kết thúc màn
     * chơi), sẽ trở lại làm quái"* — ultimate của Đẻ mướn không có đếm lùi, nên "hết màn" là mốc
     * duy nhất để dọn. Gọi TRƯỚC khi khoá trạng thái để trận sau bắt đầu với đúng quân của nó.
     */
    const reverted = this.revertConverted();
    if (reverted > 0) {
      for (const u of this.units) {
        if (u.kind === 'mother' && u.alive) u.ultUntil = 0;
      }
    }
    this.events.push({ type: 'waveClear', power: this.wave });
    if (this.wave >= this.waves.length) {
      this.status = 'won';
      this.events.push({ type: 'waveClear', power: 999 });
    } else {
      this.status = 'waveClear';
    }
  }

  private lose(): void {
    this.status = 'lost';
  }

  // ------------------------------------------------------------ tiện ích

  private dist(a: Unit, b: Unit): number {
    return Math.hypot(b.x - a.x, b.y - a.y);
  }

  private distXY(ax: number, ay: number, bx: number, by: number): number {
    return Math.hypot(bx - ax, by - ay);
  }

  private moveToward(u: Unit, tx: number, ty: number, speed: number, dt: number): void {
    const dx = tx - u.x;
    const dy = ty - u.y;
    const len = Math.hypot(dx, dy) || 1;
    this.moveDir(u, dx / len, dy / len, speed, dt);
  }

  private moveDir(u: Unit, nx: number, ny: number, speed: number, dt: number): void {
    u.x += nx * speed * dt;
    u.y += ny * speed * dt;
    if (Math.abs(nx) > 0.05) u.facing = nx > 0 ? 1 : -1;
  }

  private pickNearestEnemy(u: Unit, maxDist: number): Unit | undefined {
    let best: Unit | undefined;
    let bestD = maxDist;
    for (const e of this.units) {
      if (!e.alive || e.team !== 'enemy') continue;
      const d = this.dist(u, e);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  /**
   * AI của TƯỚNG: ưu tiên địch đang tiến gần NHÀ, không phải địch gần mình nhất.
   * Đây là thứ làm hàng thủ "biết phòng thủ" thay vì đuổi bừa — và là lý do
   * ít quái lọt qua để đập Nhà.
   */
  private pickDefenseTarget(u: Unit, maxDist: number): Unit | undefined {
    let best: Unit | undefined;
    let bestScore = Infinity;
    for (const e of this.units) {
      if (!e.alive || e.team !== 'enemy') continue;
      const d = this.dist(u, e);
      if (d > maxDist) continue;
      const distToCore = this.distXY(e.x, e.y, this.core.x, this.core.y);
      const score = distToCore + d * 0.15;
      if (score < bestScore) {
        bestScore = score;
        best = e;
      }
    }
    return best;
  }

  private pickNearestHero(u: Unit, maxDist: number): Unit | undefined {
    let best: Unit | undefined;
    let bestD = maxDist;
    for (const h of this.units) {
      if (!h.alive || h.team !== 'hero') continue;
      const d = this.dist(u, h);
      if (d < bestD) {
        bestD = d;
        best = h;
      }
    }
    return best;
  }

  private enemiesNear(x: number, y: number, radius: number): Unit[] {
    const out: Unit[] = [];
    for (const e of this.units) {
      if (!e.alive || e.team !== 'enemy') continue;
      if (this.distXY(x, y, e.x, e.y) <= radius + e.radius) out.push(e);
    }
    out.sort((a, b) => this.distXY(x, y, a.x, a.y) - this.distXY(x, y, b.x, b.y));
    return out;
  }

  private pushFloat(x: number, y: number, text: string, color: number, kind: FloatText['kind'], ttl = 0.9): void {
    if (this.floats.length > 160) return;
    this.floats.push({ x, y, text, color, ttl, kind });
  }

  /**
   * Phát 1 vụ hiệu ứng. Mọi hiệu ứng trong game đi qua ĐÂY để:
   *   · tự đánh số `id` tăng dần (render biết vụ nào MỚI ⇒ bắn hạt đúng lúc)
   *   · chặn trần số lượng (chống vỡ hiệu năng khi 90 quái chết cùng lúc)
   */
  private burst(b: Omit<EffectBurst, 'id' | 'maxTtl'> & { maxTtl?: number }): void {
    if (this.bursts.length > 420) return;
    this.bursts.push({ ...b, maxTtl: b.maxTtl ?? b.ttl, id: this.burstId++ });
  }

  /** Hướng từ A tới B (radian) — dùng cho hiệu ứng va chạm có hướng. */
  private angleBetween(ax: number, ay: number, bx: number, by: number): number {
    return Math.atan2(by - ay, bx - ax);
  }

  /**
   * BỘ HIỆU ỨNG VA CHẠM ĐẦY ĐỦ: một cú đánh không chỉ là 1 vệt sáng.
   * Gồm: tia va chạm có hướng → vệt chém → tia lửa bắn ra → loé sáng (nếu chí mạng).
   * `power` 0..1 quyết định số tia lửa và cỡ.
   */
  private hitBursts(x: number, y: number, angle: number, color: number, power: number, crit: boolean): void {
    this.burst({ x, y, radius: 16 + power * 22, color, ttl: 0.2, maxTtl: 0.2, kind: 'impact', angle, scale: crit ? 1.35 : 1 });
    this.burst({ x, y, radius: 18 + power * 26, color, ttl: 0.22, maxTtl: 0.22, kind: 'slash', angle: angle + Math.PI * 0.5, len: 34 + power * 46 });
    const n = crit ? 6 : 3;
    for (let i = 0; i < n; i++) {
      const a = angle + this.fxRng.range(-0.9, 0.9) + (i - n / 2) * 0.16;
      this.burst({ x, y, radius: 6 + power * 6, color, ttl: 0.3, maxTtl: 0.3, kind: 'spark', angle: a, len: 22 + power * 34 });
    }
    if (crit) {
      this.burst({ x, y, radius: 30 + power * 30, color: 0xffffff, ttl: 0.16, maxTtl: 0.16, kind: 'flash', scale: 0.8 });
      this.burst({ x, y, radius: 26 + power * 26, color, ttl: 0.32, maxTtl: 0.32, kind: 'ring' });
      this.hitStop = Math.max(this.hitStop, 0.055);
      this.shockPower = Math.max(this.shockPower, 0.35);
    } else {
      this.hitStop = Math.max(this.hitStop, 0.018 + power * 0.02);
    }
  }

  /**
   * BỘ HIỆU ỨNG NỔ DIỆN RỘNG: vòng xung kích + vết nứt đất + bụi + tia lửa toả tròn.
   * Đây là thứ khiến tuyệt kỹ "hoành tráng" thay vì chỉ 1 vòng tròn mờ.
   */
  private blastBursts(x: number, y: number, radius: number, color: number, power: number): void {
    this.burst({ x, y, radius, color, ttl: 0.5, maxTtl: 0.5, kind: 'ring', scale: 1.15 });
    this.burst({ x, y, radius: radius * 0.9, color: 0xffffff, ttl: 0.2, maxTtl: 0.2, kind: 'flash', scale: 0.7 });
    this.burst({ x, y, radius: radius * 0.86, color, ttl: 0.9, maxTtl: 0.9, kind: 'crack' });
    this.burst({ x, y, radius: radius * 0.5, color: 0xd6c4a8, ttl: 0.6, maxTtl: 0.6, kind: 'dust', scale: 1.1 });
    const n = 6 + Math.round(power * 6);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + this.fxRng.range(-0.2, 0.2);
      this.burst({ x, y, radius: 8 + power * 10, color, ttl: 0.4, maxTtl: 0.4, kind: 'spark', angle: a, len: radius * (0.45 + power * 0.4) });
    }
    this.shockPower = Math.max(this.shockPower, power);
  }

  /** Snapshot gọn cho test cân bằng. */
  snapshot() {
    return {
      time: this.time,
      wave: this.wave,
      status: this.status,
      coreHp: this.core.hp,
      gold: this.gold,
      heroes: this.heroes.length,
      enemies: this.enemies.length,
      heroMinHp: this.heroes.length ? Math.min(...this.heroes.map((h) => h.hp / h.base.hp)) : 1,
      kills: this.stats.kills,
      damageDealt: Math.round(this.stats.damageDealt),
      damageTaken: Math.round(this.stats.damageTaken),
      floored: this.stats.damageFloored,
      blocked: this.stats.blocked,
      enraged: this.time - this.waveStartedAt > ENRAGE_AFTER_SEC,
    };
  }
}

export { DEF_WEIGHT };
